<template>
  <div class="cms-activity-form">
    <!-- Header -->
    <cms-form-header
      :title="isEditing ? $t('cms.activities.editActivity') : $t('cms.activities.createActivity')"
      :disabled="isFormFieldsDisabled"
      @back="goBack"
      @save-draft="saveDraft"
      @save-publish="saveAndPublish"
    >
      <template v-if="canPreviewOnWebsite || activitySetupRoute" #extra-actions>
        <cms-button
          v-if="canPreviewOnWebsite"
          variant="secondary"
          size="md"
          icon="eye"
          @click="openWebsitePreview"
        >
          {{ $t('cms.activities.previewOnWebsite') }}
        </cms-button>
        <router-link
          v-if="activitySetupRoute"
          :to="activitySetupRoute"
          class="cms-btn cms-btn-secondary cms-btn-md cms-form-header-link"
          target="_blank"
        >
          <v-icon icon="settings" size="sm" />
          {{ $t('cms.activities.goToActivitySetup') }}
        </router-link>
      </template>
    </cms-form-header>

    <!-- Main Layout -->
    <cms-form-layout>
      <!-- Main Content Area -->
      <form @submit.prevent="handleSubmit">
        <!-- Retrieved Activity Section -->
        <cms-form-section
          :title="$t('cms.activities.retrievedActivity')"
          note="Select an activity from the BS to relate with the website activity. This will automatically fill some important information."
        >
          <div class="cms-form-group retrieve-activity">
            <v-select
              id="case-create-path-select"
              v-model="activity"
              :options="activities"
              class="select-search mb-8"
              :disabled="isEditing"
              :searchable="!isEditing"
              :clearable="!isEditing"
              @search="onSearch"
              @update:modelValue="selectActivity"
            >
              <template v-slot:no-options>
                {{ $t("clientTour.templateNoOptions") }}
              </template>

              <template v-slot:option="option">
                <div class="d-center">
                  <span>{{ option?.name }}</span>
                </div>
              </template>

              <template v-slot:selected-option="option">
                <div class="selected d-center py-8">
                  <span>{{ option?.name }}</span>
                </div>
              </template>
            </v-select>

            <div v-if="isLoadingActivity" class="cms-loading-activity">
              <div class="cms-loading-spinner"></div>
              <p>Loading activity details...</p>
            </div>

            <div v-else-if="selectedActivity" class="cms-selected-activity">
              <div class="cms-selected-activity-info">
                <h4>{{ selectedActivity.name }}</h4>
                <p>{{ selectedActivity.origin?.name }} - {{ selectedActivity.destination?.name }}</p>
              </div>
              <cms-button v-if="!isEditing" variant="icon" icon="x" @click="clearActivity" />
            </div>

            <p v-if="selectedActivity" class="cms-form-help">
              <router-link
                target="_blank"
                :to="{
                  name: 'activities.show',
                  params: {
                    activitiesId: selectedActivity.id,
                  },
                }"
                class="btn-link"
              >
                {{ $t("cms.activities.bsActivityLinkSubtitle") }}
              </router-link>
            </p>

            <div v-if="!isEditing && selectedActivity" class="cms-copy-from-activity">
              <label class="cms-input-label">{{ $t('cms.activities.copyFromActivity') }}</label>
              <v-select
                v-model="copySourceActivityId"
                :options="cmsActivityOptions"
                label="label"
                :reduce="reduceActivityOption"
                :clearable="true"
                :searchable="true"
                class="cms-relationship-select"
                :placeholder="$t('cms.activities.copyFromActivity')"
                @update:modelValue="openCopyFromActivityModal"
              />
            </div>
          </div>
        </cms-form-section>

        <!-- CMS Activity Fields -->
        <transition name="cms-form-section-fade" mode="out-in">
          <div v-if="selectedActivity" key="cms-fields" class="cms-wp-section">
            <h3 class="cms-wp-section-title">{{ $t("cms.activities.cmsFields") }}</h3>
            <div class="cms-wp-section-content">
              <!-- Translations Tabs -->
              <cms-translation-tabs :languages="languages" v-model="currentLanguage">
                <template #default="{ locale }">
                  <div class="cms-form-grid" :key="`cms-translation-fields-${locale}-${translationFieldsKey}`">
                    <div class="cms-form-group cms-form-group-full">
                      <label>
                        {{ $t("cms.activities.activityTitle") }}
                        <span class="cms-required">*</span>
                        ({{ locale.toUpperCase() }})
                      </label>
                      <input 
                        v-model="getTranslation(locale).title" 
                        type="text" 
                        class="cms-input"
                        :disabled="isFormFieldsDisabled"
                      />
                    </div>
                    <div class="cms-form-group">
                      <label>
                        {{ $t("cms.activities.slug") }}
                        <span class="cms-required">*</span>
                        ({{ locale.toUpperCase() }})
                      </label>
                      <input 
                        v-model="getTranslation(locale).slug" 
                        type="text" 
                        class="cms-input"
                        :disabled="isFormFieldsDisabled"
                      />
                    </div>
                    <div class="cms-form-group">
                      <label>
                        {{ $t("cms.activities.seoTitle") }}
                        <span class="cms-required">*</span>
                        ({{ locale.toUpperCase() }})
                      </label>
                      <input 
                        v-model="getTranslation(locale).seo_title" 
                        type="text" 
                        class="cms-input"
                        :disabled="isFormFieldsDisabled"
                      />
                    </div>
                    <cms-textarea
                      v-model="getTranslation(locale).seo_description"
                      :label="`${$t('cms.activities.seoDescription')} * (${locale.toUpperCase()})`"
                      :rows="3"
                      :full-width="true"
                      :disabled="isFormFieldsDisabled"
                      :max-length="160"
                      :show-counter="true"
                    />
                    <cms-list-input
                      v-model="getTranslation(locale).keywords"
                      :label="`${$t('cms.activities.keywords')} (${locale.toUpperCase()})`"
                      :placeholder="$t('cms.pages.seoKeywordsPlaceholder')"
                      :add-button-text="$t('cms.activities.addKeyword')"
                      :help-text="$t('cms.activities.keywordsHelp')"
                      :full-width="true"
                      :disabled="isFormFieldsDisabled"
                    />
                  </div>
                </template>
              </cms-translation-tabs>
            </div>
          </div>
          <div v-else key="cms-fields-placeholder" class="cms-wp-section cms-placeholder-section">
            <div class="cms-placeholder-content">
              <div class="cms-placeholder-icon">
                <v-icon icon="edit" size="lg" />
              </div>
              <h3 class="cms-placeholder-title">{{ $t("cms.activities.cmsFields") }}</h3>
              <p class="cms-placeholder-description">
                Select an activity above to start customizing content, SEO settings, and translations for your website.
              </p>
              <div class="cms-placeholder-features">
                <div class="cms-placeholder-feature">
                  <v-icon icon="globe" size="sm" />
                  <span>Multi-language content</span>
                </div>
                <div class="cms-placeholder-feature">
                  <v-icon icon="search" size="sm" />
                  <span>SEO optimization</span>
                </div>
                <div class="cms-placeholder-feature">
                  <v-icon icon="tag" size="sm" />
                  <span>Keywords & metadata</span>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Activity Configuration -->
        <transition name="cms-form-section-fade" mode="out-in">
          <cms-form-section v-if="selectedActivity" key="configuration" :title="$t('cms.activities.configuration')">
            <div class="cms-configuration-grid">
              <div class="cms-readonly-field">
                <label class="cms-readonly-field__label">{{ $t('cms.activities.modality') }}</label>
                <p class="cms-readonly-field__value">{{ linkedActivityModalityLabel }}</p>
                <p class="cms-form-help">{{ $t('template.modalityInheritedHelp') }}</p>
              </div>

              <cms-input
                v-model.number="form.duration"
                :label="$t('cms.activities.duration')"
                type="number"
                placeholder="e.g., 6"
                :disabled="isFormFieldsDisabled"
                help-text="Duration of the activity (editable)"
              />

              <cms-select
                v-model="form.type_of_booking"
                :label="$t('cms.activities.typeOfBooking')"
                :options="bookingTypeOptions"
                :disabled="isFormFieldsDisabled"
              />

              <cms-select
                v-model="form.difficulty"
                :label="$t('cms.activities.difficulty')"
                :options="difficultyOptions"
                :disabled="isFormFieldsDisabled"
              />

              <cms-input
                v-model="form.general_location"
                :label="$t('cms.activities.generalLocation')"
                :disabled="isFormFieldsDisabled"
              />

              <cms-input
                v-model="form.general_country"
                :label="$t('cms.activities.generalCountry')"
                :disabled="isFormFieldsDisabled"
              />

              <cms-input
                v-model="form.general_accomodation"
                :label="$t('cms.activities.generalAccommodation')"
                placeholder="e.g., 3-star hotels"
                :disabled="isFormFieldsDisabled"
              />

              <template v-if="isGroupModality">
                <cms-input
                  v-model.number="basePriceSingleRoom"
                  :label="$t('cms.activities.basePriceSingleRoom')"
                  type="number"
                  placeholder="0"
                  :required="true"
                  :disabled="isFormFieldsDisabled"
                  :help-text="$t('cms.activities.basePriceSingleRoomHelp')"
                />
                <cms-input
                  v-model.number="basePriceDoubleRoom"
                  :label="$t('cms.activities.basePriceDoubleRoom')"
                  type="number"
                  placeholder="0"
                  :required="true"
                  :disabled="isFormFieldsDisabled"
                  :help-text="$t('cms.activities.basePriceDoubleRoomHelp')"
                />
                <cms-date-range-list-input
                  v-model="form.date_ranges"
                  :label="$t('cms.activities.groupTripDateRanges')"
                  :help-text="$t('cms.activities.groupTripDateRangesHelp')"
                  :required="true"
                  :full-width="true"
                  :duration="form.duration"
                  :guider-options="guiderOptions"
                  :default-single-room-price="form.base_price_single_room"
                  :default-double-room-price="form.base_price_double_room"
                  :disabled="isFormFieldsDisabled"
                />
              </template>

              <template v-else>
                <cms-input
                  v-model.number="form.base_price"
                  :label="$t('cms.activities.basePrice')"
                  type="number"
                  placeholder="0"
                  :required="true"
                  :disabled="isFormFieldsDisabled"
                  help-text="Base price for this activity (EUR)"
                />
              </template>

              <div class="cms-form-group cms-available-languages">
                <label>{{ $t('cms.activities.availableLanguages') }}</label>
                <div class="cms-language-options">
                  <label
                    v-for="(label, value) in languageOptions"
                    :key="value"
                    class="cms-language-option"
                    :class="{ 'is-selected': isLanguageSelected(value) }"
                  >
                    <input
                      type="checkbox"
                      class="cms-checkbox"
                      :checked="isLanguageSelected(value)"
                      :disabled="isFormFieldsDisabled"
                      @change="toggleLanguage(value, ($event.target as HTMLInputElement).checked)"
                    />
                    <span class="cms-checkbox-label">{{ label }}</span>
                  </label>
                </div>
              </div>

              <div class="cms-form-group cms-services-card">
                <label>{{ $t("cms.activities.services") }}</label>
                <div class="cms-service-options">
                  <label class="cms-service-option" :class="{ 'is-selected': form.transport === true }">
                    <input
                      v-model="form.transport"
                      :true-value="true"
                      :false-value="false"
                      type="checkbox"
                      class="cms-checkbox"
                      :disabled="isFormFieldsDisabled"
                    />
                    <span class="cms-checkbox-label">{{ $t("cms.activities.luggageTransport") }}</span>
                  </label>
                  <label class="cms-service-option" :class="{ 'is-selected': form.transfer === true }">
                    <input
                      v-model="form.transfer"
                      :true-value="true"
                      :false-value="false"
                      type="checkbox"
                      class="cms-checkbox"
                      :disabled="isFormFieldsDisabled"
                    />
                    <span class="cms-checkbox-label cms-service-label-with-tooltip">
                      {{ $t("cms.activities.transferService") }}
                      <span class="cms-service-tooltip" :title="$t('cms.activities.serviceBookingTooltip')">
                        <v-icon icon="info" size="xs" />
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div class="cms-map-services-group">
                <cms-input
                  v-model="form.map"
                  :label="$t('cms.activities.map')"
                  :help-text="$t('cms.activities.mapHelp')"
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  type="url"
                  :full-width="true"
                  :disabled="isFormFieldsDisabled"
                />
              </div>
            </div>

            <!-- Submit Button -->
            <div class="cms-form-actions">
              <button type="button" class="cms-btn cms-btn-primary" :disabled="!canSave" @click="saveCmsActivity">
                <v-icon icon="download" size="sm" />
                {{ $t("general.button.save") }}
              </button>
            </div>
          </cms-form-section>
          <cms-form-section v-else key="configuration-placeholder" :title="$t('cms.activities.configuration')">
            <div class="cms-placeholder-content">
              <div class="cms-placeholder-icon">
                <v-icon icon="settings" size="lg" />
              </div>
              <p class="cms-placeholder-description">
                Configure activity settings like pricing, difficulty, booking type, and more once you select an activity.
              </p>
              <div class="cms-placeholder-features">
                <div class="cms-placeholder-feature">
                  <v-icon icon="dollar-sign" size="sm" />
                  <span>Pricing & booking</span>
                </div>
                <div class="cms-placeholder-feature">
                  <v-icon icon="map-pin" size="sm" />
                  <span>Location details</span>
                </div>
                <div class="cms-placeholder-feature">
                  <v-icon icon="calendar" size="sm" />
                  <span>Duration & dates</span>
                </div>
              </div>
            </div>
          </cms-form-section>
        </transition>

        <!-- Stages Section (Itinerary) -->
        <transition name="cms-form-section-fade" mode="out-in">
          <cms-form-section
            v-if="selectedActivity"
            key="stages"
            :title="$t('cms.activities.stages')"
            :note="$t('cms.activities.stagesHelp')"
          >
            <template #header>
              <div class="cms-itinerary-toolbar">
                <div v-if="form.stages.length > 0" class="cms-itinerary-toolbar__summary">
                  <span class="cms-itinerary-chip">
                    {{ formatStageDaysSummary(form.stages.length) }}
                  </span>
                  <span v-if="totalStageDistanceKm != null" class="cms-itinerary-chip cms-itinerary-chip--accent">
                    {{ $t('cms.activities.stagesTotalDistance', { distance: formatStageDistance(totalStageDistanceKm) }) }}
                  </span>
                </div>
                <div class="cms-itinerary-toolbar__actions">
                  <cms-button
                    v-if="form.stages.length > 0"
                    variant="secondary"
                    size="sm"
                    @click="toggleAllStages"
                  >
                    {{ allStagesExpanded ? $t('cms.activities.collapseAllDays') : $t('cms.activities.expandAllDays') }}
                  </cms-button>
                  <cms-button
                    variant="secondary"
                    icon="download"
                    size="sm"
                    :disabled="isFormFieldsDisabled || isImportingStages"
                    @click="importFromActivityBuilder"
                  >
                    {{ isImportingStages ? $t('general.loading') : $t('cms.activities.importFromBuilder') }}
                  </cms-button>
                  <cms-button
                    v-if="form.stages.length > 0"
                    variant="secondary"
                    icon="plus"
                    size="sm"
                    @click="addStage"
                  >
                    {{ $t('cms.activities.addDay') }}
                  </cms-button>
                </div>
              </div>
            </template>

            <div v-if="form.stages.length === 0" class="cms-itinerary-empty-state">
              <cms-empty-state
                :message="$t('cms.activities.noStagesYet')"
                :hint="$t('cms.activities.noStagesHint')"
              />
              <div class="cms-itinerary-empty-actions">
                <cms-button variant="primary" icon="plus" @click="addStage">
                  {{ $t('cms.activities.addFirstDay') }}
                </cms-button>
                <cms-button
                  variant="secondary"
                  icon="download"
                  :disabled="isFormFieldsDisabled || isImportingStages"
                  @click="importFromActivityBuilder"
                >
                  {{ $t('cms.activities.importFromBuilder') }}
                </cms-button>
              </div>
            </div>

            <div v-else class="cms-itinerary-timeline">
              <div
                v-for="(stage, index) in form.stages"
                :key="stage.id"
                class="cms-itinerary-day-row"
                :class="{ 'is-last': index === form.stages.length - 1 }"
              >
                <div class="cms-itinerary-spine" aria-hidden="true">
                  <span
                    class="cms-itinerary-node"
                    :class="{ 'is-expanded': isDayExpanded(stage.id) }"
                  >
                    {{ formatStageOrder(stage.order) }}
                  </span>
                  <span v-if="index < form.stages.length - 1" class="cms-itinerary-connector" />
                </div>

                <article
                  class="cms-itinerary-day"
                  :class="{ 'is-expanded': isDayExpanded(stage.id) }"
                >
                  <div class="cms-itinerary-day-header">
                    <button
                      type="button"
                      class="cms-itinerary-day-toggle"
                      :aria-expanded="isDayExpanded(stage.id)"
                      :aria-label="isDayExpanded(stage.id) ? 'Collapse day' : 'Expand day'"
                      @click="toggleDay(stage.id)"
                    >
                      <v-icon
                        icon="chevron-down"
                        size="sm"
                        class="cms-itinerary-day-toggle-icon"
                      />
                    </button>

                    <div
                      v-if="stage.featured_image"
                      class="cms-itinerary-day-thumb"
                      @click="toggleDay(stage.id)"
                    >
                      <img :src="stage.featured_image" :alt="getStageTranslation(stage, currentLanguage).title || ''" />
                    </div>

                    <div class="cms-itinerary-day-header-main" @click="toggleDay(stage.id)">
                      <div class="cms-itinerary-day-summary">
                        <div class="cms-itinerary-day-title-row">
                          <span class="cms-itinerary-day-label">
                            {{ $t('cms.activities.stageDayLabel', { order: stage.order }) }}
                          </span>
                          <input
                            :value="getStageTranslation(stage, currentLanguage).title"
                            type="text"
                            class="cms-itinerary-day-title-input"
                            :placeholder="`${$t('cms.activities.stageTitlePlaceholder')} (${currentLanguage.toUpperCase()})`"
                            @click.stop
                            @input="updateStageTranslation(stage, currentLanguage, 'title', ($event.target as HTMLInputElement).value)"
                          />
                        </div>
                        <div v-if="getStageRouteSummary(stage) || getStageMetaLine(stage)" class="cms-itinerary-day-meta">
                          <span
                            class="cms-itinerary-day-type"
                            :class="stage.is_stage ? 'is-route' : 'is-location'"
                          >
                            {{ stage.is_stage ? $t('cms.activities.dayTypeRoute') : $t('cms.activities.dayTypeLocation') }}
                          </span>
                          <span v-if="getStageRouteSummary(stage)" class="cms-itinerary-day-route">
                            {{ getStageRouteSummary(stage) }}
                          </span>
                          <span v-if="getStageMetaLine(stage)" class="cms-itinerary-day-meta-line">
                            {{ getStageMetaLine(stage) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="cms-itinerary-day-header-aside">
                      <div
                        v-if="stage.is_stage && stage.distance_km != null"
                        class="cms-itinerary-distance-pill"
                      >
                        <span class="cms-itinerary-distance-pill__value">{{ formatStageDistance(stage.distance_km) }}</span>
                        <span class="cms-itinerary-distance-pill__unit">km</span>
                      </div>
                      <div class="cms-itinerary-day-header-actions">
                        <cms-button
                          variant="icon"
                          icon="trash"
                          size="sm"
                          :title="$t('general.remove')"
                          @click="removeStage(index)"
                        />
                      </div>
                    </div>
                  </div>

                  <transition name="cms-accordion">
                    <div v-show="isDayExpanded(stage.id)" class="cms-itinerary-day-content">
                      <div class="cms-itinerary-day-body">
                        <section class="cms-itinerary-panel">
                          <header class="cms-itinerary-panel__head">
                            <h4 class="cms-itinerary-panel__title">{{ $t('cms.activities.stageContentSection') }}</h4>
                          </header>
                          <div class="cms-itinerary-panel__body cms-itinerary-panel__body--media">
                            <div class="cms-itinerary-day-image">
                              <cms-image-upload
                                :key="`stage-img-${stage.id}`"
                                v-model="stage.featured_image"
                                :label="$t('cms.activities.stageImage')"
                                :help-text="$t('cms.activities.stageImageHelp')"
                                :full-width="true"
                                :initial-media="getStageInitialMedia(stage)"
                                @uploaded="handleStageImageUpload(stage.id, $event)"
                              />
                            </div>
                            <div class="cms-itinerary-day-fields-panel">
                              <cms-translation-tabs :languages="languages" v-model="currentLanguage">
                                <template #default="{ locale }">
                                  <div class="cms-itinerary-day-fields">
                                    <cms-input
                                      :model-value="getStageTranslation(stage, locale).featured_image_alt"
                                      :label="`${$t('cms.activities.featuredImageAlt')} (${locale.toUpperCase()})`"
                                      :help-text="$t('cms.activities.featuredImageAltHelp')"
                                      :full-width="true"
                                      :show-tooltip-on-hover="false"
                                      @update:model-value="updateStageTranslation(stage, locale, 'featured_image_alt', $event)"
                                    />
                                    <cms-editor
                                      :model-value="getStageTranslation(stage, locale).description"
                                      :label="`${$t('cms.activities.stageDescription')} (${locale.toUpperCase()})`"
                                      :full-width="true"
                                      @update:model-value="updateStageTranslation(stage, locale, 'description', $event)"
                                    />
                                  </div>
                                </template>
                              </cms-translation-tabs>
                            </div>
                          </div>
                        </section>

                        <section class="cms-itinerary-panel cms-itinerary-panel--route">
                          <header class="cms-itinerary-panel__head">
                            <h4 class="cms-itinerary-panel__title">{{ $t('cms.activities.stageRouteSection') }}</h4>
                            <div class="cms-itinerary-day-type-switch" role="group" :aria-label="$t('cms.activities.stageRouteSection')">
                              <button
                                type="button"
                                class="cms-itinerary-day-type-switch__btn"
                                :class="{ 'is-active': stage.is_stage }"
                                :disabled="isFormFieldsDisabled"
                                @click="stage.is_stage = true"
                              >
                                {{ $t('cms.activities.dayTypeRoute') }}
                              </button>
                              <button
                                type="button"
                                class="cms-itinerary-day-type-switch__btn"
                                :class="{ 'is-active': !stage.is_stage }"
                                :disabled="isFormFieldsDisabled"
                                @click="stage.is_stage = false"
                              >
                                {{ $t('cms.activities.dayTypeLocation') }}
                              </button>
                            </div>
                          </header>
                          <div class="cms-itinerary-panel__body">
                            <p class="cms-itinerary-route-hint">
                              {{ stage.is_stage ? $t('cms.activities.stageRouteHint') : $t('cms.activities.stageLocationHint') }}
                            </p>

                            <cms-translation-tabs :languages="languages" v-model="currentLanguage">
                              <template #default="{ locale }">
                                <div v-if="stage.is_stage" class="cms-itinerary-route-fields">
                                  <div
                                    v-if="getStageTranslation(stage, locale).from || getStageTranslation(stage, locale).to"
                                    class="cms-itinerary-route-preview"
                                  >
                                    <span>{{ getStageTranslation(stage, locale).from || '…' }}</span>
                                    <span class="cms-itinerary-route-preview__arrow" aria-hidden="true">→</span>
                                    <span>{{ getStageTranslation(stage, locale).to || '…' }}</span>
                                  </div>
                                  <div class="cms-itinerary-route-grid">
                                    <div class="cms-itinerary-route-row cms-itinerary-route-row--stacked">
                                      <cms-input
                                        :model-value="getStageTranslation(stage, locale).from || ''"
                                        :label="`${$t('cms.activities.stageFrom')} (${locale.toUpperCase()})`"
                                        :full-width="true"
                                        @update:model-value="updateStageTranslation(stage, locale, 'from', $event)"
                                      />
                                      <cms-input
                                        :model-value="getStageTranslation(stage, locale).to || ''"
                                        :label="`${$t('cms.activities.stageTo')} (${locale.toUpperCase()})`"
                                        :full-width="true"
                                        @update:model-value="updateStageTranslation(stage, locale, 'to', $event)"
                                      />
                                    </div>
                                    <div class="cms-itinerary-route-row">
                                      <cms-select
                                        :model-value="stage.mode ?? null"
                                        :label="$t('cms.activities.stageMode')"
                                        :options="stageModeOptions"
                                        :placeholder="$t('cms.activities.stageMode')"
                                        @update:model-value="setStageMode(stage, $event)"
                                      />
                                      <cms-input
                                        :model-value="stage.distance_km ?? ''"
                                        type="number"
                                        allow-decimal
                                        step="0.1"
                                        :label="$t('cms.activities.stageDistance')"
                                        placeholder="0"
                                        @update:model-value="stage.distance_km = parseStageDistance($event)"
                                      />
                                    </div>
                                  </div>
                                </div>
                                <cms-input
                                  v-else
                                  :model-value="getStageTranslation(stage, locale).location_label || ''"
                                  :label="`${$t('cms.activities.stageLocation')} (${locale.toUpperCase()})`"
                                  :full-width="true"
                                  @update:model-value="updateStageTranslation(stage, locale, 'location_label', $event)"
                                />
                              </template>
                            </cms-translation-tabs>
                          </div>
                        </section>
                      </div>
                    </div>
                  </transition>
                </article>
              </div>

              <div class="cms-itinerary-actions">
                <cms-button variant="secondary" icon="plus" @click="addStage">
                  {{ $t('cms.activities.addDay') }}
                </cms-button>
              </div>
            </div>
          </cms-form-section>
          <cms-form-section
            v-else
            key="stages-placeholder"
            :title="$t('cms.activities.stages')"
            note="Daily itinerary stages will appear here once you select an activity"
          >
            <div class="cms-placeholder-content">
              <div class="cms-placeholder-icon">
                <v-icon icon="route" size="lg" />
              </div>
              <p class="cms-placeholder-description">
                View and manage the daily itinerary stages that will be automatically imported from your selected activity.
              </p>
              <div class="cms-placeholder-features">
                <div class="cms-placeholder-feature">
                  <v-icon icon="clock" size="sm" />
                  <span>Day-by-day schedule</span>
                </div>
                <div class="cms-placeholder-feature">
                  <v-icon icon="map" size="sm" />
                  <span>Route information</span>
                </div>
                <div class="cms-placeholder-feature">
                  <v-icon icon="edit" size="sm" />
                  <span>Editable content</span>
                </div>
              </div>
            </div>
          </cms-form-section>
        </transition>

        <!-- Activity-level guiders removed for group trips — assign guides per trip in Group trip dates -->
        <cms-form-section
          v-if="false && selectedActivity && form.modality === 'group'"
          :title="$t('cms.activities.guiders')"
        >
          <template #header>
            <router-link :to="{ name: 'cms.guiders.create' }" class="cms-btn cms-btn-secondary" target="_blank">
              <v-icon icon="plus" size="sm" />
              {{ $t('cms.activities.createNewGuider') }}
            </router-link>
          </template>

          <div class="cms-form-group">
            <label class="cms-input-label">{{ $t('cms.activities.guiders') }}</label>
            <v-select
              v-model="form.guiders"
              :options="guiderOptions"
              multiple
              label="label"
              :reduce="reduceGuiderOption"
              class="cms-relationship-select"
              :close-on-select="false"
              :clearable="false"
              :searchable="true"
              :loading="isLoadingGuiders"
            >
              <template v-slot:no-options>
                {{ $t('cms.guides.searchPlaceholder') }}
              </template>
            </v-select>
            <p class="cms-form-help">
              {{ $t('cms.activities.guidersHelp') || 'Select guiders to associate with this activity. Create new guiders in the Guiders section.' }}
            </p>
          </div>

          <div v-if="selectedGuiders.length > 0" class="cms-selected-items">
            <div
              v-for="guider in selectedGuiders"
              :key="guider.id"
              class="cms-selected-guider-item"
            >
              <div class="cms-selected-guider-image" v-if="guider.featuredImage">
                <img :src="guider.featuredImage" :alt="guider.title" />
              </div>
              <div class="cms-selected-guider-image-placeholder" v-else>
                <v-icon icon="user" size="md" />
              </div>
              <div class="cms-selected-guider-content">
                <h4 class="cms-selected-guider-title">{{ guider.title }}</h4>
                <p v-if="guider.userName" class="cms-selected-guider-user">
                  <v-icon icon="user" size="xs" />
                  {{ guider.userName }}
                </p>
                <p v-if="guider.tagline" class="cms-selected-guider-tagline">{{ guider.tagline }}</p>
              </div>
              <button
                type="button"
                class="cms-selected-guider-remove"
                @click="removeGuider(guider.id)"
                :title="$t('general.remove')"
              >
                <v-icon icon="x" size="sm" />
              </button>
            </div>
          </div>
        </cms-form-section>

        <!-- Reviews Section -->
        <cms-form-section :title="$t('cms.activities.reviews')">
          <template #header>
            <router-link :to="{ name: 'cms.reviews' }" class="cms-btn cms-btn-secondary">
              <!-- <v-icon icon="plus" size="sm" />
              {{ $t('cms.activities.createNewReview') }} -->
              +
            </router-link>
          </template>

          <div v-if="!selectedActivity" class="cms-placeholder-content">
            <div class="cms-placeholder-icon">
              <v-icon icon="message-square" size="lg" />
            </div>
            <p class="cms-placeholder-description">
              Showcase customer testimonials and reviews to build credibility and help potential customers make decisions.
            </p>
            <div class="cms-placeholder-features">
              <div class="cms-placeholder-feature">
                <v-icon icon="star" size="sm" />
                <span>Star ratings</span>
              </div>
              <div class="cms-placeholder-feature">
                <v-icon icon="user" size="sm" />
                <span>Customer stories</span>
              </div>
              <div class="cms-placeholder-feature">
                <v-icon icon="thumbs-up" size="sm" />
                <span>Social proof</span>
              </div>
            </div>
          </div>

          <template v-else>
            <p class="cms-section-note">Associated reviews from the Reviews section</p>
            <!-- TODO -> add a way to connect reviews to the activity -->
            <cms-empty-state
              v-if="form.reviews.length === 0"
              :message="$t('cms.activities.noReviewsAssociated')"
              hint="Create reviews in the Reviews section to associate them with this activity"
            />

            <cms-readonly-item
              v-for="(review, index) in form.reviews"
              :key="index"
              :title="review.title"
              :subtitle="`${review.name} - ${formatDate(review.review_date)}`"
              :badges="[`⭐ ${review.rating}/5`]"
              :content="review.content"
              @remove="removeReview(index)"
            />
          </template>
        </cms-form-section>

        <!-- Overview Content Section -->
        <cms-form-section
          v-if="selectedActivity"
          :title="$t('cms.activities.overviewContent')"
        >
          <cms-translation-tabs :languages="languages" v-model="currentLanguage">
            <template #default="{ locale }">
              <cms-editor
                :key="`cms-translation-content-${locale}-${translationFieldsKey}`"
                v-model="getTranslation(locale).content"
                :label="`${$t('cms.activities.aboutThisTrip')} (${locale.toUpperCase()})`"
                :full-width="true"
                :disabled="isFormFieldsDisabled"
              />
              <p class="cms-form-help">{{ $t('cms.activities.overviewWebsiteHelp') }}</p>
            </template>
          </cms-translation-tabs>

          <div class="cms-overview-cards">
            <div
              v-for="placement in overviewPlacements"
              :key="placement.key"
              class="cms-overview-card"
            >
              <h4 class="cms-overview-card-title">{{ $t(placement.labelKey) }}</h4>
              <cms-translation-tabs :languages="faqLanguages" v-model="faqTabLanguage">
                <template #default="{ locale }">
                  <cms-input
                    v-model="getOverviewFaqTranslation(placement.key, locale).section_label"
                    :label="`${$t('cms.activities.overviewSectionLabel')} (${locale.toUpperCase()})`"
                    :full-width="true"
                    :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                  />
                  <cms-input
                    v-model="getOverviewFaqTranslation(placement.key, locale).question"
                    :label="`${$t('cms.activities.overviewTitle')} (${locale.toUpperCase()})`"
                    :full-width="true"
                    :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                  />
                  <cms-input
                    v-if="'hasSubLabel' in placement && placement.hasSubLabel"
                    v-model="getOverviewFaqTranslation(placement.key, locale).sub_label"
                    :label="`${$t('cms.activities.overviewSubLabel')} (${locale.toUpperCase()})`"
                    :full-width="true"
                    :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                  />
                  <cms-editor
                    v-if="placement.contentType === 'prose'"
                    v-model="getOverviewFaqTranslation(placement.key, locale).answer"
                    :label="`${$t('cms.activities.faqAnswer')} (${locale.toUpperCase()})`"
                    :full-width="true"
                    :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                  />
                  <cms-list-input
                    v-else
                    v-model="getOverviewFaqTranslation(placement.key, locale).items"
                    :label="`${$t('cms.activities.overviewListItems')} (${locale.toUpperCase()})`"
                    :add-button-text="$t('cms.activities.addListItem')"
                    :full-width="true"
                    :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                  />
                </template>
              </cms-translation-tabs>
            </div>
          </div>
        </cms-form-section>

        <!-- FAQ Section -->
        <cms-form-section :title="$t('cms.activities.faq')" :note="$t('cms.activities.faqNote')">
          <p v-if="selectedActivity" class="cms-form-help">{{ $t('cms.activities.faqWebsiteHelp') }}</p>
          <div v-if="!selectedActivity" class="cms-placeholder-content">
            <div class="cms-placeholder-icon">
              <v-icon icon="help-circle" size="lg" />
            </div>
            <p class="cms-placeholder-description">
              Create a comprehensive FAQ section to address common customer questions and reduce support inquiries.
            </p>
            <div class="cms-placeholder-features">
              <div class="cms-placeholder-feature">
                <v-icon icon="message-circle" size="sm" />
                <span>Q&A format</span>
              </div>
              <div class="cms-placeholder-feature">
                <v-icon icon="search" size="sm" />
                <span>Searchable content</span>
              </div>
              <div class="cms-placeholder-feature">
                <v-icon icon="edit-3" size="sm" />
                <span>Rich text answers</span>
              </div>
            </div>
          </div>

          <template v-else>
            <div v-if="form.faq.length === 0" class="cms-faq-empty-state">
              <v-icon icon="help-circle" size="lg" />
              <p>{{ $t('cms.activities.noFaqsYet') }}</p>
              <p class="cms-faq-empty-hint">{{ $t('cms.activities.addFirstFaqHint') }}</p>
            </div>

            <div v-else class="cms-faq-list">
              <div 
                v-for="(faqItem, index) in form.faq" 
                :key="faqItem.id || `temp-${index}`" 
                class="cms-faq-item-wrapper"
                :class="{ 'cms-faq-item-expanded': expandedFaqs[index] }"
              >
                <div 
                  class="cms-faq-item-header"
                  @click="toggleFaqAccordion(index)"
                >
                  <div class="cms-faq-item-number">
                    <v-icon 
                      :icon="expandedFaqs[index] ? 'chevron-down' : 'chevron-right'" 
                      size="sm" 
                      class="cms-faq-accordion-icon"
                    />
                    <v-icon icon="help-circle" size="sm" />
                    <span>{{ $t('cms.activities.faq') }} {{ index + 1 }}</span>
                    <span v-if="getFaqPreviewQuestion(faqItem)" class="cms-faq-preview">
                      {{ getFaqPreviewQuestion(faqItem).length > 50 ? getFaqPreviewQuestion(faqItem).substring(0, 50) + '...' : getFaqPreviewQuestion(faqItem) }}
                    </span>
                  </div>
                  <div class="cms-faq-item-actions" @click.stop>
                    <cms-button 
                      variant="danger" 
                      icon="trash" 
                      size="sm"
                      :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                      :title="$t('general.remove')"
                      @click="removeFaqItem(index)"
                    >
                      {{ $t("general.remove") }}
                    </cms-button>
                  </div>
                </div>

                <div class="cms-faq-item-content" :class="{ 'cms-faq-content-expanded': expandedFaqs[index] }" @click.stop>
                  <cms-translation-tabs :languages="faqLanguages" v-model="faqTabLanguage" @click.stop>
                    <template #default="{ locale }">
                      <div class="cms-faq-question-wrapper">
                        <cms-input 
                          v-model="getFaqTranslation(faqItem, locale).question" 
                          :label="`${$t('cms.activities.faqQuestion')} (${locale.toUpperCase()})`" 
                          :full-width="true"
                          :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                          :placeholder="$t('cms.activities.faqQuestionPlaceholder')"
                        />
                      </div>
                      <div class="cms-faq-answer-wrapper">
                        <cms-editor 
                          v-model="getFaqTranslation(faqItem, locale).answer" 
                          :label="`${$t('cms.activities.faqAnswer')} (${locale.toUpperCase()})`"
                        />
                      </div>
                    </template>
                  </cms-translation-tabs>
                </div>
              </div>
            </div>

            <div class="cms-faq-actions">
              <cms-button 
                variant="secondary" 
                icon="plus" 
                      :disabled="isFormFieldsDisabled || (!isEditing && isLoadingFaqs)"
                @click="addFaqItem"
              >
                {{ $t("cms.activities.addFaqItem") }}
              </cms-button>
            </div>
          </template>
        </cms-form-section>
      </form>

      <!-- Sidebar -->
      <template #sidebar>
        <cms-sidebar>
          <!-- Publish Controls -->
          <cms-sidebar-section :title="$t('cms.activities.publish')">
            <div v-if="!selectedActivity" class="cms-sidebar-placeholder">
              <div class="cms-sidebar-placeholder-icon">
                <v-icon icon="upload" size="md" />
              </div>
              <p class="cms-sidebar-placeholder-text">
                Publishing options will be available after selecting an activity.
              </p>
            </div>
            <template v-else>
              <cms-select
                v-model="form.is_public"
                :label="$t('cms.activities.status')"
                :disabled="isFormFieldsDisabled"
                :options="{
                  false: $t('cms.activities.statusOptions.draft'),
                  true: $t('cms.activities.statusOptions.published'),
                }"
              />

              <div class="cms-form-group">
                <label>{{ $t("cms.activities.visibility") }}</label>
                <p class="cms-form-help">
                  {{ form.is_public ? $t("cms.activities.visibilityPublic") : $t("cms.activities.visibilityPrivate") }}
                </p>
              </div>

              <label class="cms-checkbox-item">
                <input 
                  v-model="form.is_featured" 
                  type="checkbox" 
                  class="cms-checkbox" 
                  :disabled="isFormFieldsDisabled"
                  style="margin-right: 8px" 
                />
                <span class="cms-checkbox-label">{{ $t("cms.activities.isFeatured") }}</span>
              </label>
            </template>
          </cms-sidebar-section>

          <!-- Media -->
          <cms-sidebar-section :title="$t('cms.activities.media')">
            <div v-if="!selectedActivity" class="cms-sidebar-placeholder">
              <div class="cms-sidebar-placeholder-icon">
                <v-icon icon="image" size="md" />
              </div>
              <p class="cms-sidebar-placeholder-text">
                Upload featured images and gallery photos after selecting an activity and saving it as a draft.
              </p>
              <div class="cms-sidebar-placeholder-features">
                <div class="cms-sidebar-placeholder-feature">
                  <v-icon icon="camera" size="xs" />
                  <span>Featured image</span>
                </div>
                <div class="cms-sidebar-placeholder-feature">
                  <v-icon icon="grid" size="xs" />
                  <span>Photo gallery</span>
                </div>
              </div>
            </div>
            <template v-else-if="cmsActivityId">
              <cms-image-upload
                ref="featuredImageUploadRef"
                v-model="form.featured_image"
                :initial-media="featuredUploaderInitialMedia"
                :label="$t('cms.activities.featuredImage')"
                help-text="Recommended: 1920x1080px"
                @uploaded="handleFeaturedUploaded"
                @removed="handleFeaturedRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingFeaturedMedia }">
                <cms-button variant="secondary" size="sm" :disabled="!canSaveFeaturedMedia || isSyncingFeaturedMedia" @click="saveFeaturedMedia">
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingFeaturedMedia ? $t('general.button.saving') : $t("cms.activities.saveFeaturedMediaButton") }}
                </cms-button>
              </div>

              <div class="cms-sidebar-featured-alt">
                <cms-input
                  v-model="getTranslation(currentLanguage).featured_image_alt"
                  :label="`${$t('cms.activities.featuredImageAlt')} (${currentLanguage.toUpperCase()})`"
                  :help-text="$t('cms.activities.featuredImageAltHelp')"
                  :full-width="true"
                  :disabled="isFormFieldsDisabled"
                  :show-tooltip-on-hover="false"
                />
              </div>

              <cms-gallery-upload
                ref="galleryUploadRef"
                v-model="form.gallery"
                :initial-media="galleryUploaderInitialMedia"
                :label="$t('cms.activities.gallery')"
                help-text="Add multiple images to your activity gallery"
                :max-items="20"
                @uploaded="handleGalleryUploaded"
                @removed="handleGalleryRemoved"
              />
              <div class="cms-media-actions" :class="{ 'is-media-saving': isSyncingGalleryMedia }">
                <cms-button variant="secondary" size="sm" :disabled="!canSaveGalleryMedia || isSyncingGalleryMedia" @click="saveGalleryMedia">
                  <v-icon icon="upload" size="sm" />
                  {{ isSyncingGalleryMedia ? $t('general.button.saving') : $t("cms.activities.saveGalleryMediaButton") }}
                </cms-button>
              </div>
            </template>
            <template v-else>
              <div class="cms-sidebar-placeholder">
                <div class="cms-sidebar-placeholder-icon">
                  <v-icon icon="download" size="md" />
                </div>
                <p class="cms-sidebar-placeholder-text">
                  {{ $t("cms.activities.mediaUploadRequiresCreation") }}
                </p>
                <p class="cms-sidebar-placeholder-hint">
                  Save your activity as a draft first to enable media uploads.
                </p>
              </div>
            </template>
          </cms-sidebar-section>

          <!-- Additional Categories -->
          <cms-sidebar-section :title="$t('cms.activities.additionalCategories')">
            <div v-if="!selectedActivity" class="cms-sidebar-placeholder">
              <div class="cms-sidebar-placeholder-icon">
                <v-icon icon="tag" size="md" />
              </div>
              <p class="cms-sidebar-placeholder-text">
                Categorize your activity to help customers find it more easily.
              </p>
            </div>
            <div v-else class="cms-categories-section">
              <p class="cms-form-help cms-categories-help">
                {{ $t('cms.activities.additionalCategoriesHelp') }}
              </p>
              <div class="cms-categories-toolbar">
                <span
                  v-if="!isLoadingCategories && availableCategories.length > 0"
                  class="cms-categories-count"
                >
                  {{ $t('cms.activities.categoriesSelectedSummary', { count: form.categories.length }) }}
                </span>
                <cms-button
                  variant="secondary"
                  size="sm"
                  icon="plus"
                  class="cms-categories-add-btn"
                  :disabled="isFormFieldsDisabled || (!isEditing && isLoadingCategories)"
                  @click="showCategoryModal = true"
                >
                  {{ $t('general.button.add') }}
                </cms-button>
              </div>
              <div v-if="isLoadingCategories" class="cms-loading-categories">
                <div class="cms-loading-spinner"></div>
                <p>{{ $t('general.loading') }}</p>
              </div>
              <div v-else-if="availableCategories.length === 0" class="cms-empty-categories">
                <div class="cms-empty-categories-icon" aria-hidden="true">
                  <v-icon icon="tag" size="md" />
                </div>
                <p class="cms-empty-text">{{ $t('cms.activities.noCategoriesAvailable') }}</p>
                <p class="cms-empty-hint">{{ $t('cms.activities.addCategoryHint') }}</p>
              </div>
              <div v-else class="cms-categories-list" role="list">
                <label
                  v-for="category in availableCategories"
                  :key="category.id"
                  class="cms-category-checkbox-item"
                  role="listitem"
                >
                  <input
                    v-model="form.categories"
                    type="checkbox"
                    :value="category.id"
                    class="cms-checkbox"
                    :disabled="isFormFieldsDisabled"
                  />
                  <span class="cms-category-thumb" aria-hidden="true">
                    <img v-if="category.icon" :src="category.icon" alt="" />
                    <v-icon v-else icon="tag" size="sm" />
                  </span>
                  <span class="cms-category-text">
                    <span class="cms-checkbox-label">{{ category.name }}</span>
                    <span v-if="category.slug" class="cms-category-slug">{{ category.slug }}</span>
                  </span>
                </label>
              </div>
            </div>
          </cms-sidebar-section>

          <cms-sidebar-section :title="$t('cms.activities.suggestedActivities')">
            <div v-if="!selectedActivity" class="cms-sidebar-placeholder">
              <p class="cms-sidebar-placeholder-text">
                {{ $t('cms.activities.suggestedActivitiesHelp') }}
              </p>
            </div>
            <template v-else>
              <v-select
                v-model="form.suggested_activities"
                :options="cmsActivityOptions"
                multiple
                label="label"
                :reduce="reduceActivityOption"
                class="cms-relationship-select"
                :close-on-select="false"
                :clearable="false"
                :disabled="isFormFieldsDisabled"
              />
              <p class="cms-form-help">{{ $t('cms.activities.suggestedActivitiesHelp') }}</p>
            </template>
          </cms-sidebar-section>
        </cms-sidebar>
      </template>
    </cms-form-layout>

    <!-- Copy from CMS Activity Modal -->
    <div v-if="showCopyModal" class="cms-modal-overlay" @click="closeCopyModal">
      <div class="cms-modal cms-modal-large" @click.stop>
        <div class="cms-modal-header">
          <h3>{{ $t('cms.activities.copyFromActivityTitle') }}</h3>
          <button type="button" class="cms-modal-close-btn" @click="closeCopyModal" :aria-label="$t('general.close')">
            <v-icon icon="x" size="sm" />
          </button>
        </div>
        <div class="cms-modal-body">
          <p class="cms-form-help">{{ $t('cms.activities.copyFromActivityHelp') }}</p>
          <div class="cms-copy-sections">
            <label v-for="section in copySectionOptions" :key="section.key" class="cms-checkbox-item">
              <input v-model="copySections" type="checkbox" class="cms-checkbox" :value="section.key" />
              <span class="cms-checkbox-label">{{ $t(section.labelKey) }}</span>
            </label>
          </div>
          <div class="cms-modal-actions">
            <cms-button variant="secondary" @click="closeCopyModal">{{ $t('general.button.cancel') }}</cms-button>
            <cms-button
              variant="primary"
              :disabled="!copySourceActivityId || copySections.length === 0 || isCopyingActivity"
              @click="applyCopyFromActivity"
            >
              {{ isCopyingActivity ? $t('general.button.saving') : $t('cms.activities.applyCopy') }}
            </cms-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Modal -->
    <cms-category-modal
      :show="showCategoryModal"
      @close="showCategoryModal = false"
      @category-created="handleCategoryCreated"
    />
  </div>
</template>

<script lang="ts">
/**
 * CMS Activity Form
 * Data Flow:
 * 1. User selects a BS Activity from the search dropdown
 * 2. BS Activity data is retrieved (includes: name, description, stages, included/excluded info, etc.)
 * 3. CMS Activity data is loaded if it exists (includes: categories, difficulty, translations, etc.)
 * 4. User can customize CMS-specific fields and save
 *
 * Backend Requirements:
 * - GET /v2/{agency}/activity/{activityId} - Retrieve full BS Activity with all relations
 * - GET /v2/{agency}/cms-activities?activity_id={activityId} - Check if CMS activity exists
 * - POST /v2/{agency}/cms-activities - Create new CMS activity
 * - PUT /v2/{agency}/cms-activities/{id} - Update existing CMS activity
 * - POST /v2/{agency}/cms-activities/{id}/publish - Publish CMS activity
 *
 * Expected BS Activity Structure:
 * - name, description, total_days, travel_type
 * - origin, destination, featured_image
 * - included_information.description, excluded_information.description
 * - extras_information.description, before_information.description, after_information.description
 * - activity_days[] (for stages)
 */

import { defineComponent, ref, reactive, computed, getCurrentInstance, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import vSelect from "vue-select";
import useConcepts from "@/helpers/Concepts";
import { getAppLanguageLocales } from '@/helpers/ConceptHelper';
import { useAppLanguages } from '@/composables/useAppLanguages';
import type { Translation, ActivityForm, Stage, StageTranslation, CmsTripDateRange, FaqItem } from "@/types/cms";

const createEmptyTripDateRange = (): CmsTripDateRange => ({
  start_date: "",
  end_date: "",
  base_price_single_room: 0,
  base_price_double_room: 0,
  max_pax: 0,
  guider_ids: [],
});

const normalizeTripDateRangeFromApi = (r: Record<string, unknown>): CmsTripDateRange | null => {
  const startRaw = r.start_date ?? r.startDate;
  const endRaw = r.end_date ?? r.endDate;
  const normalizeYmd = (value: unknown): string => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const date = new Date(value as string);
    if (Number.isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const start_date = normalizeYmd(startRaw);
  if (!start_date) return null;
  const end_date = normalizeYmd(endRaw) || start_date;
  const guiderIdsRaw = r.guider_ids ?? r.guiderIds;
  const guider_ids = Array.isArray(guiderIdsRaw)
    ? guiderIdsRaw.filter((id): id is number => typeof id === "number" && id > 0)
    : [];
  return {
    start_date,
    end_date,
    base_price_single_room:
      Number(r.base_price_single_room ?? r.basePriceSingleRoom ?? 0) || 0,
    base_price_double_room:
      Number(r.base_price_double_room ?? r.basePriceDoubleRoom ?? 0) || 0,
    max_pax: Number(r.max_pax ?? r.maxPax ?? 0) || 0,
    booked_pax: Number(r.booked_pax ?? r.bookedPax ?? 0) || 0,
    guider_ids,
  };
};
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useI18n } from "vue-i18n";
import {
  extractCmsActivityData,
  normalizeCmsActivity,
  normalizeResource,
  extractRelationship,
} from "@/composables/useCmsActivityData";
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from "@/helpers/cmsMediaUpload";
import { runCmsMediaSync } from "@/helpers/cmsMediaSaveFlow";
import { mapActivityDataToForm, mapBasicActivityData, mapActivityDaysToStages } from "@/composables/useActivityMapping";

const OVERVIEW_FAQ_PLACEMENTS = [
  { key: 'overview_why_choose', labelKey: 'cms.activities.overviewWhyChoose', contentType: 'prose' },
  { key: 'overview_highlights', labelKey: 'cms.activities.overviewHighlights', contentType: 'list' },
  { key: 'overview_suitability', labelKey: 'cms.activities.overviewSuitability', contentType: 'list', hasSubLabel: true },
  { key: 'overview_considerations', labelKey: 'cms.activities.overviewConsiderations', contentType: 'list' },
] as const;

type OverviewPlacementKey = typeof OVERVIEW_FAQ_PLACEMENTS[number]['key'];

interface ActivitySelectOption {
  value: number;
  label: string;
}

// Import CMS Components
import {
  CmsFormHeader,
  CmsFormLayout,
  CmsFormSection,
  CmsInput,
  CmsTextarea,
  CmsSelect,
  CmsDateRangeListInput,
  CmsButton,
  CmsEditor,
  CmsListInput,
  CmsSidebar,
  CmsSidebarSection,
  CmsReadonlyItem,
  CmsEmptyState,
  CmsTranslationTabs,
  CmsImageUpload,
  CmsGalleryUpload,
  CmsCategoryModal,
} from "@/components/cms";

export default defineComponent({
  name: "TheCmsActivityForm",
  components: {
    vSelect,
    VIcon,
    CmsFormHeader,
    CmsFormLayout,
    CmsFormSection,
    CmsInput,
    CmsTextarea,
    CmsSelect,
    CmsDateRangeListInput,
    CmsButton,
    CmsEditor,
    CmsListInput,
    CmsSidebar,
    CmsSidebarSection,
    CmsReadonlyItem,
    CmsEmptyState,
    CmsTranslationTabs,
    CmsImageUpload,
    CmsGalleryUpload,
    CmsCategoryModal,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();
    const authStore = useAuthStore();
    const agency = computed(() => authStore.agency);
    const agencySlug = computed(() => agency.value?.slug ?? null);
    // TODO -> check agency and adjust accordingly if its needed

    const instance = getCurrentInstance();
    const $toast = instance?.appContext.config.globalProperties.$toast;

    const showToast = (type: "success" | "error", message: string) => {
      const toastHandler = ($toast as any)?.[type];

      if (typeof toastHandler === "function") {
        toastHandler(message);
      }
    };

    const extractErrorMessage = (error: any, fallbackMessage?: string) => {
      const defaultMessage = fallbackMessage ?? t("general.errorMessage");
      const responseData = error?.response?.data;

      if (responseData) {
        if (typeof responseData === "string") {
          return responseData;
        }

        if (typeof responseData === "object") {
          if (responseData.message && typeof responseData.message === "string") {
            return responseData.message;
          }

          if (responseData.error && typeof responseData.error === "string") {
            return responseData.error;
          }

          const errors = responseData.errors;
          if (Array.isArray(errors) && errors.length > 0) {
            return errors[0];
          }

          if (errors && typeof errors === "object") {
            const firstKey = Object.keys(errors)[0];
            if (firstKey) {
              const firstError = errors[firstKey];
              if (Array.isArray(firstError) && firstError.length > 0) {
                return firstError[0];
              }
              if (typeof firstError === "string") {
                return firstError;
              }
            }
          }
        }
      }

      if (error?.message && typeof error.message === "string") {
        return error.message;
      }

      return defaultMessage;
    };

    const showApiError = (error: any, fallbackMessage?: string) => {
      showToast("error", extractErrorMessage(error, fallbackMessage));
    };

    const requiredTranslationFields: Array<keyof Translation> = [
      "title",
      "content",
      "slug",
      "seo_title",
      "seo_description",
    ];

    const stripHtmlContent = (value: string | null | undefined) => {
      if (!value) {
        return "";
      }

      return value
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    };

    const isTranslationFieldFilled = (field: keyof Translation, value: any) => {
      if (typeof value === "string") {
        const normalized = field === "content" ? stripHtmlContent(value) : value.trim();
        return normalized.length > 0;
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== null && value !== undefined;
    };

    const getTranslationFieldLabel = (field: keyof Translation) => {
      switch (field) {
        case "title":
          return t("cms.activities.title");
        case "content":
          return t("cms.activities.content");
        case "slug":
          return t("cms.activities.slug");
        case "seo_title":
          return t("cms.activities.seoTitle");
        case "seo_description":
          return t("cms.activities.seoDescription");
        default:
          return field;
      }
    };

    const buildValidationMessage = (languageLabel: string, fieldLabels: string) => {
      const template = t("cms.activities.translationValidationError", {
        language: languageLabel,
        fields: fieldLabels,
      });

      if (template && template !== "cms.activities.translationValidationError") {
        return template;
      }

      return `Please fill the following fields for ${languageLabel}: ${fieldLabels}`;
    };

    interface TranslationValidationResult {
      valid: boolean;
      languageLabel?: string;
      fieldLabels?: string;
    }

    const getTranslationForLocale = (locale: string, createIfMissing = false) => {
      if (createIfMissing) {
        return getTranslation(locale);
      }

      return form.translations.find(
        (translation) => normalizeLocaleKey(translation.locale) === normalizeLocaleKey(locale),
      ) || null;
    };

    const buildTranslationEntries = (createMissing = false) => {
      if (languages.value && languages.value.length > 0) {
        return languages.value.map((language: any) => ({
          locale: language.shortName,
          label: language.label,
          translation: getTranslationForLocale(language.shortName, createMissing),
        }));
      }

      return form.translations.map((translation: Translation) => ({
        locale: translation.locale,
        label: translation.locale?.toUpperCase() ?? translation.locale,
        translation: createMissing ? getTranslation(translation.locale) : translation,
      }));
    };

    const translationsAreComplete = computed(() => {
      const entries = buildTranslationEntries(false);

      if (!entries.length) {
        return false;
      }

      for (const entry of entries) {
        if (!entry.translation) {
          return false;
        }

        const missingFields = requiredTranslationFields.filter(
          (field) => !isTranslationFieldFilled(field, (entry.translation as any)?.[field])
        );
        if (missingFields.length > 0) {
          return false;
        }
      }

      return true;
    });

    const buildTranslationsValidationResult = (createMissing = false): TranslationValidationResult => {
      const entries = buildTranslationEntries(createMissing);

      // Require at least one complete translation (not all locales)
      let atLeastOneComplete = false;
      let firstIncomplete: { languageLabel: string; fieldLabels: string } | null = null;

      for (const entry of entries) {
        const translation = entry.translation as Translation | null;
        const missingFields = requiredTranslationFields.filter(
          (field) => !isTranslationFieldFilled(field, translation?.[field as keyof Translation])
        );

        if (missingFields.length === 0) {
          atLeastOneComplete = true;
          break;
        }
        if (!firstIncomplete) {
          const fieldLabels = missingFields.map((field) => getTranslationFieldLabel(field)).join(", ");
          const languageLabel = entry.label || entry.locale?.toUpperCase() || entry.locale;
          firstIncomplete = { languageLabel, fieldLabels };
        }
      }

      if (atLeastOneComplete) {
        return { valid: true };
      }
      return {
        valid: false,
        languageLabel: firstIncomplete?.languageLabel,
        fieldLabels: firstIncomplete?.fieldLabels,
      };
    };

    const validateTranslations = () => {
      const result = buildTranslationsValidationResult(true);

      if (!result.valid) {
        const languageLabel =
          result.languageLabel || t("cms.activities.translationGenericLanguage") || "selected language";
        const fieldLabels =
          result.fieldLabels || requiredTranslationFields.map((field) => getTranslationFieldLabel(field)).join(", ");
        showToast("error", buildValidationMessage(languageLabel, fieldLabels));
        return false;
      }

      return true;
    };

    const isEditing = computed(() => !!route.params.id);
    /** In edit mode, fields stay editable once the page is open; loading flags only gate create flow. */
    const isFormFieldsDisabled = computed(() => {
      if (isEditing.value) {
        return false;
      }
      return isLoadingActivity.value || isLoadingCmsActivity.value;
    });
    const activity = ref<any>(null);
    const activities = ref<any[]>([]);
    const selectedActivity = ref<any>(null);
    const isLoadingActivity = ref(false);
    const cmsActivityId = ref<number | null>(null);
    const previewToken = ref<string | null>(null);
    const websiteSiteUrl = ref<string>('');
    const publishProcessing = ref(false);
    const isLoadingCmsActivity = ref(false);
    /** Bumped after CMS translations are mapped so tab fields remount with API data. */
    const translationFieldsKey = ref(0);
    const featuredImageUploadRef = ref<any>(null);
    const galleryUploadRef = ref<any>(null);
    const featuredUploaderInitialMedia = ref<any[]>([]);
    const galleryUploaderInitialMedia = ref<any[]>([]);
    const stageInitialMedia = ref<Record<string, any[]>>({});
    const guiders = ref<any[]>([]);
    const isLoadingGuiders = ref(false);
    const availableCategories = ref<any[]>([]);
    const isLoadingCategories = ref(false);
    const showCategoryModal = ref(false);

    // Concepts and languages
    const concepts = useConcepts();
    const { languages, locales, resolveStoredLocale } = useAppLanguages();
    const currentLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));
    const faqTabLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));
    // Form state - populated from backend API
    const form = reactive<ActivityForm>({
      title: "",
      slug: "",
      modality: "self-guided",
      start_date: "",
      end_date: "",
      date_ranges: [createEmptyTripDateRange()],
      duration: "",
      base_price: 0,
      base_price_single_room: 0,
      base_price_double_room: 0,
      transfer: null,
      transport: null,
      general_location: "",
      general_country: "",
      from_location: "",
      to_location: "",
      featured_image: "",
      gallery: [],
      is_public: false,
      is_featured: false,
      categories: [],
      difficulty: "",
      available_languages: [],
      general_accomodation: "",
      group_size: 0,
      guiders: [] as number[],
      reviews: [],
      content: "",
      additional_info: "",
      after_info: "",
      included_information: [""],
      not_included_information: [""],
      extra_information: "",
      stages: [],
      suggested_activities: [],
      map: "",
      faq: [],
      type_of_booking: "enquiry",
      seo_title: "",
      seo_description: "",
      keywords: [],
      translations: [
        { locale: "en", title: "", content: "", slug: "", seo_title: "", seo_description: "", keywords: [], featured_image_alt: "" },
        { locale: "es", title: "", content: "", slug: "", seo_title: "", seo_description: "", keywords: [], featured_image_alt: "" },
        { locale: "nl", title: "", content: "", slug: "", seo_title: "", seo_description: "", keywords: [], featured_image_alt: "" },
      ],
    });

    const modalityOptions = {
      "self-guided": "Self-Guided",
      group: "Group",
    };

    const linkedActivityModality = computed(
      () => selectedActivity.value?.modality || "self-guided"
    );

    const isGroupModality = computed(() => linkedActivityModality.value === "group");

    const linkedActivityModalityLabel = computed(
      () =>
        modalityOptions[linkedActivityModality.value as keyof typeof modalityOptions] ||
        linkedActivityModality.value
    );

    const bookingTypeOptions = {
      enquiry: "Enquiry",
      booking: "Booking",
      instant: "Instant",
      hybrid: "Hybrid",
    };

    const difficultyOptions = {
      "": "None",
      easy: "Easy",
      medium: "Medium",
      intermediate: "Intermediate",
      hard: "Hard",
    };

    const stageModeOptions = computed(() => ({
      walk: t('cms.activities.stageModeWalk'),
      bike: t('cms.activities.stageModeBike'),
    }));

    const overviewPlacements = OVERVIEW_FAQ_PLACEMENTS;
    const overviewFaqs = ref<Record<OverviewPlacementKey, FaqItem>>({} as Record<OverviewPlacementKey, FaqItem>);
    const overviewFaqIdsToDelete = ref<number[]>([]);

    const createEmptyOverviewFaq = (placement: OverviewPlacementKey): FaqItem => {
      const placementConfig = OVERVIEW_FAQ_PLACEMENTS.find((p) => p.key === placement);
      return {
        placement,
        content_type: placementConfig?.contentType || 'prose',
        is_public: true,
        translations: faqLocales.value.map((locale) => ({
          locale,
          question: '',
          answer: '',
          section_label: '',
          sub_label: '',
          items: [],
        })),
      };
    };

    const initOverviewFaqs = () => {
      const next = {} as Record<OverviewPlacementKey, FaqItem>;
      OVERVIEW_FAQ_PLACEMENTS.forEach(({ key }) => {
        next[key] = overviewFaqs.value[key] || createEmptyOverviewFaq(key);
      });
      overviewFaqs.value = next;
    };

    const cmsActivityOptions = ref<ActivitySelectOption[]>([]);
    const isImportingStages = ref(false);
    const copySourceActivityId = ref<number | null>(null);
    const showCopyModal = ref(false);
    const isCopyingActivity = ref(false);
    const copySections = ref<string[]>([
      'translations',
      'configuration',
      'stages',
      'overview',
      'faq',
      'categories',
      'suggestedActivities',
    ]);

    const copySectionOptions = [
      { key: 'translations', labelKey: 'cms.activities.copySections.translations' },
      { key: 'configuration', labelKey: 'cms.activities.copySections.configuration' },
      { key: 'stages', labelKey: 'cms.activities.copySections.stages' },
      { key: 'overview', labelKey: 'cms.activities.copySections.overview' },
      { key: 'faq', labelKey: 'cms.activities.copySections.faq' },
      { key: 'categories', labelKey: 'cms.activities.copySections.categories' },
      { key: 'suggestedActivities', labelKey: 'cms.activities.copySections.suggestedActivities' },
    ];

    const reduceActivityOption = (option: ActivitySelectOption) => option.value;

    const activitySetupRoute = computed(() => {
      if (!isEditing.value || !selectedActivity.value?.id) {
        return null;
      }
      return {
        name: 'activities.setup.show',
        params: { activitiesId: selectedActivity.value.id },
      };
    });

    const capturePreviewToken = (cmsActivityData: any) => {
      if (!cmsActivityData || typeof cmsActivityData !== 'object') {
        return;
      }
      const token =
        cmsActivityData.preview_token ||
        cmsActivityData.previewToken ||
        cmsActivityData.attributes?.preview_token ||
        cmsActivityData.attributes?.previewToken ||
        null;
      if (typeof token === 'string' && token.trim()) {
        previewToken.value = token.trim();
      }
    };

    const loadWebsiteSiteUrl = async () => {
      const envWebsiteUrl = String(import.meta.env.VITE_WEBSITE_URL || '').trim();
      if (envWebsiteUrl) {
        websiteSiteUrl.value = envWebsiteUrl.replace(/\/+$/, '');
        return;
      }

      if (!agencySlug.value) {
        websiteSiteUrl.value = '';
        return;
      }
      try {
        const response = await api.settingsService.getCmsSettings(agencySlug.value);
        const data = response?.data?.data || response?.data || response || {};
        const url = data.site_url || data.siteUrl || '';
        websiteSiteUrl.value = typeof url === 'string' ? url.trim().replace(/\/+$/, '') : '';
      } catch (error) {
        console.error('Error loading CMS site URL:', error);
        websiteSiteUrl.value = '';
      }
    };

    const loadCmsActivityOptions = async () => {
      if (!agencySlug.value) return;
      try {
        const response = await api.activityService.listCmsActivities(agencySlug.value, {
          perPage: 200,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        cmsActivityOptions.value = list
          .map((item: any) => {
            const normalized = normalizeResource(item) || item;
            if (cmsActivityId.value && normalized.id === cmsActivityId.value) {
              return null;
            }
            const translations = extractRelationship(normalized, 'translations');
            const translationList = normalizeResource(translations);
            const translation = Array.isArray(translationList) ? translationList[0] : translationList;
            return {
              value: normalized.id,
              label: translation?.title || normalized.title || normalized.name || `#${normalized.id}`,
            };
          })
          .filter((option: ActivitySelectOption | null): option is ActivitySelectOption => option !== null);
      } catch (error) {
        console.error('Failed to load CMS activities', error);
        cmsActivityOptions.value = [];
      }
    };

    // Categories are now loaded from API - categoryOptions removed

    const languageOptions = {
      en: "English",
      es: "Spanish",
      nl: "Dutch",
    };

    const isLanguageSelected = (value: string) => form.available_languages.includes(value);

    const toggleLanguage = (value: string, checked: boolean) => {
      const index = form.available_languages.indexOf(value);

      if (checked && index === -1) {
        form.available_languages.push(value);
      }

      if (!checked && index > -1) {
        form.available_languages.splice(index, 1);
      }
    };


    let editModeRequestId = 0;

    const initializeEditMode = async () => {
      if (!isEditing.value || !agencySlug.value) {
        return;
      }

      const cmsIdParam = Number(route.params.id);
      if (!cmsIdParam || Number.isNaN(cmsIdParam)) {
        return;
      }

      const requestId = ++editModeRequestId;
      isLoadingActivity.value = true;
      isLoadingCmsActivity.value = true;

      const slug = agencySlug.value;
      let cmsActivityData: any = null;

      try {
        const response = await api.activityService.getCmsActivity(slug, cmsIdParam);

        cmsActivityData = extractCmsActivityData(response);

        if (!cmsActivityData) {
          cmsActivityData = normalizeCmsActivity(response?.data || response);
        }

        if (!cmsActivityData) {
          showToast(
            "error",
            extractErrorMessage({ response: { data: response?.data ?? response } }, t("general.errorMessage"))
          );
          return;
        }

        if (requestId !== editModeRequestId) {
          return;
        }

        cmsActivityId.value = cmsActivityData.id;
        capturePreviewToken(cmsActivityData);

        let bsActivityData: any = null;
        const activityRelationship = extractRelationship(cmsActivityData, "activity");
        const bsActivityId =
          normalizeResource(activityRelationship)?.id || cmsActivityData.activity_id || cmsActivityData.activityId;

        if (bsActivityId) {
          try {
            bsActivityData = await api.activityService.retrieveActivityWithDetails(slug, bsActivityId);
          } catch (error) {
            bsActivityData = normalizeResource(activityRelationship);
          }
        }

        if (!bsActivityData) {
          bsActivityData = normalizeResource(activityRelationship);
        }

        if (requestId !== editModeRequestId) {
          return;
        }

        if (bsActivityData) {
          const option = {
            ...bsActivityData,
            label: bsActivityData.name,
          };
          activities.value = [option];
          activity.value = activities.value[0];
          mapActivityDataToForm(bsActivityData, form, getTranslation, { skipTranslations: true });
        } else if (cmsActivityData.activity) {
          const fallbackActivity = {
            ...cmsActivityData.activity,
            label: cmsActivityData.activity.name,
          };
          activities.value = [fallbackActivity];
          activity.value = activities.value[0];
          mapBasicActivityData(cmsActivityData.activity, form);
        }

        // Load guiders and categories first so they're available when mapping
        await loadGuiders();
        await loadCategories();

        if (requestId !== editModeRequestId) {
          return;
        }

        mapCmsActivityDataToForm(cmsActivityData);
        translationFieldsKey.value += 1;
        initOverviewFaqs();

        if (bsActivityData) {
          selectedActivity.value = bsActivityData;
        } else if (cmsActivityData.activity) {
          selectedActivity.value = cmsActivityData.activity;
        }
      } catch (error) {
        console.error("Error loading CMS activity:", error);
        showApiError(error);
      } finally {
        isLoadingActivity.value = false;
        isLoadingCmsActivity.value = false;
      }

      if (requestId !== editModeRequestId) {
        return;
      }

      try {
        await loadFaqs();
      } catch (error) {
        console.error("Error loading FAQs:", error);
      }
    };

    // Ensure edit mode initializes when slug or route param change
    watch(
      [agencySlug, () => route.params.id],
      async ([slug, id]) => {
        if (!isEditing.value) {
          return;
        }

        const numericId = Number(id);
        if (!slug || !numericId || Number.isNaN(numericId)) {
          return;
        }

        await initializeEditMode();
      },
      { immediate: true }
    );

    watch(agencySlug, async (slug) => {
      if (slug) {
        await Promise.all([loadCmsActivityOptions(), loadWebsiteSiteUrl()]);
      }
    }, { immediate: true });

    watch(cmsActivityId, () => {
      loadCmsActivityOptions();
    });

    // Activity search functions
    const onSearch = async (searchTerm: string) => {
      if (isEditing.value || searchTerm.length < 1 || !agencySlug.value) return;

      try {
        const filters = {
          filters: {
            status: ["active"],
            name: searchTerm,
          },
        };

        const activitiesData = await api.activityService.allPaginated(agencySlug.value, filters);

        activities.value = activitiesData.data.map((activity: any) => ({
          ...activity,
          label: activity.name,
        }));
      } catch (error) {
        console.error("Error fetching activities:", error);
        activities.value = [];
      }
    };

    const selectActivity = async (activityItem: any) => {
      if (!activityItem || isEditing.value || !agencySlug.value) return;

      activity.value = activityItem;
      selectedActivity.value = null;
      isLoadingActivity.value = true;
      isLoadingCmsActivity.value = true;

      const slug = agencySlug.value;

      if (activityItem && slug) {
        try {
          // Step 1: Retrieve full BS activity data with all related information
          const fullActivityData = await api.activityService.retrieveActivityWithDetails(slug, activityItem.id);

          const option = {
            ...activityItem,
            ...fullActivityData,
            label: fullActivityData.name,
          };
          activity.value = option;

          const optionIndex = activities.value.findIndex((item: any) => item.id === option.id);
          if (optionIndex > -1) {
            activities.value.splice(optionIndex, 1, option);
          } else {
            activities.value.push(option);
          }

          // Step 2: Map BS activity data to form (includes stages, included/excluded info, etc.)
          mapActivityDataToForm(fullActivityData, form, getTranslation, { skipTranslations: true });

          // Load categories and guiders for selection
          await loadGuiders();
          await loadCategories();

          // Step 3: Try to load existing CMS activity data for this BS activity
          try {
            let cmsActivityData = null;

            try {
              const detailedResponse = await api.activityService.getDetailedCmsActivityByActivityId(
                slug,
                activityItem.id
              );
              cmsActivityData = extractCmsActivityData(detailedResponse);
            } catch (detailError) {
              const cmsActivityResponse = await api.activityService.getCmsActivityByActivityId(slug, activityItem.id);
              cmsActivityData = extractCmsActivityData(cmsActivityResponse);
            }

            if (cmsActivityData && cmsActivityData.id) {
              cmsActivityId.value = cmsActivityData.id;
              capturePreviewToken(cmsActivityData);
              mapCmsActivityDataToForm(cmsActivityData);
              translationFieldsKey.value += 1;
              // Load FAQs for this activity
              await loadFaqs();
            } else {
              cmsActivityId.value = null;
              previewToken.value = null;
              mapActivityDataToForm(fullActivityData, form, getTranslation);
              translationFieldsKey.value += 1;
              initializeCmsActivityData();
              form.faq = [];
              expandedFaqs.value = {};
            }
          } catch (cmsError) {
            cmsActivityId.value = null;
            previewToken.value = null;
            mapActivityDataToForm(fullActivityData, form, getTranslation);
            translationFieldsKey.value += 1;
            initializeCmsActivityData();
            showApiError(cmsError);
          }

          selectedActivity.value = fullActivityData;
        } catch (error) {
          console.error("Error fetching BS Activity details:", error);
          // Fallback to basic data if full API call fails
          mapBasicActivityData(activityItem, form);
          initializeCmsActivityData();
          translationFieldsKey.value += 1;
          selectedActivity.value = activityItem;
          showApiError(error);
        } finally {
          isLoadingActivity.value = false;
          isLoadingCmsActivity.value = false;
        }
      }
    };

    const createQueryInitialized = ref(false);

    const initializeCreateFromActivityQuery = async () => {
      if (isEditing.value || createQueryInitialized.value || !agencySlug.value) {
        return;
      }

      const raw = route.query.activity_id;
      const activityId = raw ? Number(Array.isArray(raw) ? raw[0] : raw) : NaN;

      if (!activityId || Number.isNaN(activityId)) {
        return;
      }

      createQueryInitialized.value = true;

      try {
        const fullActivityData = await api.activityService.retrieveActivityWithDetails(
          agencySlug.value,
          activityId
        );

        await selectActivity({
          id: activityId,
          ...fullActivityData,
          label: fullActivityData?.name,
        });
      } catch (error) {
        console.error("Failed to preselect activity from query:", error);
        createQueryInitialized.value = false;
      }
    };

    watch(
      [agencySlug, () => route.query.activity_id, isEditing],
      async ([slug, , editing]) => {
        if (editing || !slug) {
          return;
        }

        await initializeCreateFromActivityQuery();
      },
      { immediate: true }
    );

    const mapCmsActivityDataToForm = (cmsActivity: any) => {
      const activity = normalizeResource(cmsActivity);

      if (!activity) {
        return;
      }

      mapActivityMediaCollections(cmsActivity);

      const translationsSource = extractRelationship(cmsActivity, "translations");
      const translationsList = normalizeResource(translationsSource) || [];

      const activityRelation = normalizeResource(extractRelationship(cmsActivity, "activity"));
      if (activityRelation) {
        activity.activity = activityRelation;
      }

      const featuredAlreadyMapped = !!form.featured_image;
      const galleryAlreadyMapped = Array.isArray(form.gallery) && form.gallery.length > 0;

      const readValue = (object: any, variants: string[]) => {
        if (!object) return undefined;
        for (const key of variants) {
          if (object[key] !== undefined) {
            return object[key];
          }
        }
        return undefined;
      };

      const assignValue = (object: any, variants: string[], setter: (value: any) => void) => {
        const value = readValue(object, variants);
        if (value !== undefined) {
          setter(value);
        }
      };

      assignValue(activity, ["duration"], (value) => {
        // Convert duration to number if it's a valid number, otherwise keep as string
        if (value === null || value === undefined || value === "") {
          form.duration = "";
        } else {
          const numValue = Number(value);
          form.duration = isNaN(numValue) ? String(value) : numValue;
        }
      });
      assignValue(activity, ["general_country", "generalCountry"], (value) => {
        form.general_country = value;
      });
      assignValue(activity, ["general_accomodation", "generalAccomodation"], (value) => {
        form.general_accomodation = value;
      });
      // Map categories - extract IDs from the relationship
      const categoriesRelation = extractRelationship(cmsActivity, 'categories');
      const normalizedCategories = normalizeResource(categoriesRelation);
      
      let categoryIds: number[] = [];
      if (Array.isArray(normalizedCategories)) {
        categoryIds = normalizedCategories
          .map((category: any) => {
            const normalized = normalizeResource(category) || category;
            return normalized?.id;
          })
          .filter((id: any): id is number => typeof id === 'number' && id > 0);
      } else if (normalizedCategories && typeof normalizedCategories === 'object') {
        // Single category object
        const normalized = normalizeResource(normalizedCategories) || normalizedCategories;
        if (normalized?.id) {
          categoryIds = [normalized.id];
        }
      } else if (Array.isArray(cmsActivity.categories)) {
        // Fallback: categories might be an array of IDs or objects
        categoryIds = cmsActivity.categories
          .map((category: any) => {
            if (typeof category === 'number') return category;
            const normalized = normalizeResource(category) || category;
            return normalized?.id;
          })
          .filter((id: any): id is number => typeof id === 'number' && id > 0);
      } else {
        // Fallback: try to read from activity object directly
        const categoriesValue = readValue(activity, ["categories"]);
        if (Array.isArray(categoriesValue)) {
          categoryIds = categoriesValue
            .map((cat: any) => {
              if (typeof cat === 'number') return cat;
              if (typeof cat === 'string') {
                const parsed = parseInt(cat, 10);
                return isNaN(parsed) ? null : parsed;
              }
              return cat?.id || null;
            })
            .filter((id: any): id is number => typeof id === 'number' && id > 0);
        }
      }
      
      form.categories = categoryIds;
      assignValue(activity, ["available_languages", "availableLanguages"], (value) => {
        form.available_languages = Array.isArray(value) ? value : [];
      });
      assignValue(activity, ["type_of_booking", "typeOfBooking"], (value) => {
        form.type_of_booking = value;
      });
      assignValue(activity, ["difficulty"], (value) => {
        form.difficulty = value;
      });
      assignValue(activity, ["map"], (value) => {
        form.map = value;
      });
      assignValue(activity, ["is_featured", "isFeatured"], (value) => {
        form.is_featured = value;
      });
      assignValue(activity, ["is_public", "isPublic"], (value) => {
        form.is_public = value;
      });
      assignValue(activity, ["base_price", "basePrice"], (value) => {
        form.base_price = value !== null && value !== undefined ? Number(value) : 0;
      });
      assignValue(activity, ["base_price_single_room", "basePriceSingleRoom"], (value) => {
        form.base_price_single_room = value !== null && value !== undefined ? Number(value) : 0;
      });
      assignValue(activity, ["base_price_double_room", "basePriceDoubleRoom"], (value) => {
        form.base_price_double_room = value !== null && value !== undefined ? Number(value) : 0;
      });
      assignValue(activity, ["transfer"], (value) => {
        form.transfer = value === null || value === undefined ? null : Boolean(value);
      });
      assignValue(activity, ["transport"], (value) => {
        form.transport = value === null || value === undefined ? null : Boolean(value);
      });
      assignValue(activity, ["start_date", "startDate"], (value) => {
        if (value) {
          // Handle different date formats
          if (value instanceof Date) {
            // Convert Date object to YYYY-MM-DD format
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            const day = String(value.getDate()).padStart(2, '0');
            form.start_date = `${year}-${month}-${day}`;
          } else if (typeof value === 'string') {
            // If it's already in YYYY-MM-DD format, use it directly
            if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
              form.start_date = value;
            } else {
              // Try to parse other date formats
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                form.start_date = `${year}-${month}-${day}`;
              } else {
                form.start_date = "";
              }
            }
          } else {
            form.start_date = "";
          }
        } else {
          form.start_date = "";
        }
      });

      const normalizeYmd = (value: any): string => {
        if (!value) return "";
        if (value instanceof Date) {
          const y = value.getFullYear();
          const m = String(value.getMonth() + 1).padStart(2, "0");
          const d = String(value.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        }
        if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return value;
        }
        if (typeof value === "string") {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
          }
        }
        return "";
      };

      const rangesRaw = readValue(activity, ["date_ranges", "dateRanges"]);
      if (Array.isArray(rangesRaw) && rangesRaw.length > 0) {
        form.date_ranges = rangesRaw
          .map((r: any) => normalizeTripDateRangeFromApi(r ?? {}))
          .filter((r): r is CmsTripDateRange => r !== null);
      }
      if (!form.date_ranges || form.date_ranges.length === 0) {
        if (isGroupModality.value && form.start_date) {
          const legacy = createEmptyTripDateRange();
          legacy.start_date = normalizeYmd(form.start_date);
          legacy.end_date = normalizeYmd(form.end_date) || legacy.start_date;
          legacy.base_price_single_room = form.base_price_single_room ?? 0;
          legacy.base_price_double_room = form.base_price_double_room ?? 0;
          form.date_ranges = [legacy];
        } else if (isGroupModality.value) {
          form.date_ranges = [createEmptyTripDateRange()];
        } else {
          form.date_ranges = [];
        }
      }

      // Map guiders - extract IDs from the relationship
      const guidersRelation = extractRelationship(cmsActivity, 'guiders');
      const normalizedGuiders = normalizeResource(guidersRelation);
      
      let guiderIds: number[] = [];
      if (Array.isArray(normalizedGuiders)) {
        guiderIds = normalizedGuiders
          .map((guider: any) => {
            const normalized = normalizeResource(guider) || guider;
            return normalized?.id;
          })
          .filter((id: any): id is number => typeof id === 'number' && id > 0);
      } else if (normalizedGuiders && typeof normalizedGuiders === 'object') {
        // Single guider object
        const normalized = normalizeResource(normalizedGuiders) || normalizedGuiders;
        if (normalized?.id) {
          guiderIds = [normalized.id];
        }
      } else if (Array.isArray(cmsActivity.guiders)) {
        guiderIds = cmsActivity.guiders
          .map((guider: any) => {
            const normalized = normalizeResource(guider) || guider;
            return normalized?.id;
          })
          .filter((id: any): id is number => typeof id === 'number' && id > 0);
      }
      
      form.guiders = guiderIds;
      if (
        isGroupModality.value &&
        guiderIds.length > 0 &&
        form.date_ranges.length > 0 &&
        form.date_ranges.every((r) => !r.guider_ids?.length)
      ) {
        form.date_ranges[0].guider_ids = [...guiderIds];
      }

      if (!featuredAlreadyMapped) {
        const featuredMediaUrl = readValue(activity, [
          "featured_image_url",
          "featuredImageUrl",
          "featured_image",
          "featuredImage",
        ]);

        if (featuredMediaUrl) {
          if (typeof featuredMediaUrl === "string") {
            form.featured_image = featuredMediaUrl;
          } else if (featuredMediaUrl?.url) {
            form.featured_image = featuredMediaUrl.url;
          }
        }
      }

      if (!galleryAlreadyMapped) {
        const galleryValue = readValue(activity, ["gallery"]);
        const galleryUrls = readValue(activity, ["gallery_urls", "galleryUrls"]);

        if (Array.isArray(galleryValue)) {
          form.gallery = galleryValue
            .map((item: any) => {
              if (typeof item === "string") {
                return item;
              }
              return item?.url || item?.original_url || item?.originalUrl || "";
            })
            .filter((url: string) => !!url);
        } else if (Array.isArray(galleryUrls)) {
          form.gallery = galleryUrls.filter((url: string) => !!url);
        } else {
          form.gallery = [];
        }
      }

      if (Array.isArray(translationsList) && translationsList.length > 0) {
        translationsList.forEach((translation: any) => {
          const normalizedTranslation = normalizeResource(translation) || translation;
          const locale = readValue(normalizedTranslation, ["locale"]);
          if (!locale) {
            return;
          }

          const localeKey = locale.toLowerCase();

          let formTranslation = form.translations.find((t) => t.locale.toLowerCase() === localeKey);
          if (!formTranslation) {
            formTranslation = {
              locale: localeKey,
              title: "",
              content: "",
              slug: "",
              seo_title: "",
              seo_description: "",
              keywords: [],
              featured_image_alt: "",
            };
            form.translations.push(formTranslation);
          }

          assignValue(normalizedTranslation, ["title"], (value) => {
            formTranslation.title = value ?? formTranslation.title;
          });
          assignValue(normalizedTranslation, ["content"], (value) => {
            formTranslation.content = value ?? formTranslation.content;
          });
          assignValue(normalizedTranslation, ["slug"], (value) => {
            formTranslation.slug = value ?? formTranslation.slug;
          });
          assignValue(normalizedTranslation, ["seo_title", "seoTitle"], (value) => {
            formTranslation.seo_title = value ?? formTranslation.seo_title;
          });
          assignValue(normalizedTranslation, ["seo_description", "seoDescription"], (value) => {
            formTranslation.seo_description = value ?? formTranslation.seo_description;
          });
          assignValue(normalizedTranslation, ["keywords"], (value) => {
            formTranslation.keywords = Array.isArray(value) ? value : formTranslation.keywords;
          });
          assignValue(normalizedTranslation, ["featured_image_alt", "featuredImageAlt"], (value) => {
            formTranslation.featured_image_alt = value ?? formTranslation.featured_image_alt ?? "";
          });
        });

        const fallbackTitle =
          readValue(activityRelation, ["name"]) ||
          form.title ||
          selectedActivity.value?.name ||
          "";
        form.translations.forEach((formTranslation) => {
          if (!formTranslation.title?.trim()) {
            formTranslation.title = formTranslation.seo_title?.trim() || fallbackTitle;
          }
        });
      }

      // Map stages from CMS activity
      const stagesRelation = extractRelationship(cmsActivity, 'stages');
      const normalizedStages = normalizeResource(stagesRelation);
      
      if (Array.isArray(normalizedStages)) {
        if (normalizedStages.length > 0) {
          // Map CMS stages to form
          const currentLocale = currentLanguage.value || 'en';
          stageInitialMedia.value = {};
          form.stages = normalizedStages.map((stage: any) => {
            const normalizedStage = normalizeResource(stage) || stage;
            
            // Extract translations
            const stageTranslations = extractRelationship(normalizedStage, 'translations');
            const normalizedTranslations = normalizeResource(stageTranslations);
            const translationsList = Array.isArray(normalizedTranslations) ? normalizedTranslations : [];
            
            // Find translation for current locale, or use first available, or fallback
            const currentTranslation = translationsList.find((t: any) => {
              const locale = normalizeResource(t)?.locale || t?.locale;
              return normalizeLocaleKey(locale) === normalizeLocaleKey(currentLocale);
            }) || translationsList[0] || null;
            
            const translationData = normalizeResource(currentTranslation) || currentTranslation || {};
            const title = translationData.title || normalizedStage.title || '';
            const description = translationData.description || normalizedStage.description || '';
            
            // Build translations array for all languages
            const allTranslations: StageTranslation[] = languages.value.map((lang: any) => {
              const locale = lang.shortName || lang.locale || lang;
              const translation = translationsList.find((t: any) => {
                const tLocale = normalizeResource(t)?.locale || t?.locale;
                return normalizeLocaleKey(tLocale) === normalizeLocaleKey(locale);
              });
              const tData = normalizeResource(translation) || translation || {};
              
              return {
                locale: locale,
                title: tData.title || (locale === currentLocale ? title : ''),
                description: tData.description || (locale === currentLocale ? description : ''),
                featured_image_alt: tData.featured_image_alt ?? tData.featuredImageAlt ?? '',
              };
            });
            
            // Extract featured image from media
            const { featuredUrl, featuredItems } = extractStageFeaturedMedia(normalizedStage);
            const stageId = `stage-${normalizedStage.id || Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
            if (featuredItems.length > 0) {
              stageInitialMedia.value[stageId] = featuredItems;
            }

            const isStage = normalizedStage.is_stage ?? normalizedStage.isStage ?? false;
            return {
              id: stageId,
              cmsStageId: typeof normalizedStage.id === 'number' ? normalizedStage.id : null,
              order: normalizedStage.order || 1,
              title: title,
              description: description,
              translations: allTranslations.map((tr) => {
                const tData = translationsList.find((t: any) => {
                  const tLocale = normalizeResource(t)?.locale || t?.locale;
                  return normalizeLocaleKey(tLocale) === normalizeLocaleKey(tr.locale);
                });
                const normalizedT = normalizeResource(tData) || tData || {};
                return {
                  ...tr,
                  from: normalizedT.from || '',
                  to: normalizedT.to || '',
                  location_label: normalizedT.location_label ?? normalizedT.locationLabel ?? '',
                };
              }),
              extra_data: normalizedStage.extra_data || [],
              featured_image: featuredUrl,
              gallery_text: normalizedStage.gallery_text || '',
              is_stage: Boolean(isStage),
              mode: normalizedStage.mode || null,
              distance_km: normalizedStage.distance_km ?? normalizedStage.distanceKm ?? null,
            };
          }).sort((a: Stage, b: Stage) => a.order - b.order);
        }
        // When CMS has no stages, keep stages already mapped from the BS activity.
      }

      const suggestedRelation = extractRelationship(cmsActivity, 'suggestedActivities');
      const normalizedSuggested = normalizeResource(suggestedRelation);
      if (Array.isArray(normalizedSuggested)) {
        form.suggested_activities = normalizedSuggested
          .map((item: any) => {
            const normalized = normalizeResource(item) || item;
            return normalized?.id;
          })
          .filter((id: any): id is number => typeof id === 'number' && id > 0);
      } else if (Array.isArray(cmsActivity.suggested_activities)) {
        form.suggested_activities = cmsActivity.suggested_activities
          .map((id: any) => (typeof id === 'number' ? id : null))
          .filter((id: any): id is number => typeof id === 'number' && id > 0);
      } else {
        form.suggested_activities = [];
      }
    };

    const initializeCmsActivityData = () => {
      // Called when no CMS activity exists yet for the selected BS activity
      // CMS-specific fields remain empty/default until user fills them
      form.is_featured = false;
      form.is_public = false;
      form.categories = [];
      form.available_languages = [];
      form.type_of_booking = "enquiry";
      form.difficulty = "";
      form.map = "";
      form.transfer = selectedActivity.value?.transfer ?? null;
      form.transport = selectedActivity.value?.transport ?? null;
      form.featured_image = "";
      form.gallery = [];
      featuredUploaderInitialMedia.value = [];
      galleryUploaderInitialMedia.value = [];
      featuredMediaPayload.value = null;
      galleryMediaPayload.value = null;
      pendingFeaturedDeletion.value = null;
      pendingGalleryDeletionIds.value = [];
      form.general_accomodation = "";
      form.guiders = [];
      form.reviews = [];
      form.faq = [];
      form.stages = [];
      stageInitialMedia.value = {};
      form.suggested_activities = [];
      form.date_ranges = isGroupModality.value ? [createEmptyTripDateRange()] : [];
      initOverviewFaqs();

      // Translations are initialized from the BS Activity data in mapActivityDataToForm
      // Users can then customize them as needed
    };

    const clearActivity = () => {
      if (isEditing.value) return;
      activity.value = null;
      selectedActivity.value = null;
      cmsActivityId.value = null;
      previewToken.value = null;
    };

    // Format date helper
    const formatDate = (dateString: string) => {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString();
    };

    const normalizeLocaleKey = (locale: string) => (locale || '').toLowerCase();

    const getTranslation = (locale: string): Translation => {
      const localeKey = normalizeLocaleKey(locale);
      let translation = form.translations.find((t) => normalizeLocaleKey(t.locale) === localeKey);
      if (!translation) {
        translation = {
          locale: localeKey,
          title: '',
          content: '',
          slug: '',
          seo_title: '',
          seo_description: '',
          keywords: [],
          featured_image_alt: '',
        };
        form.translations.push(translation);
      }
      return translation;
    };

    const canPreviewOnWebsite = computed(() => {
      if (!cmsActivityId.value || !previewToken.value) {
        return false;
      }
      const locale = String(currentLanguage.value || 'en').toLowerCase();
      if (locale !== 'en' && locale !== 'nl') {
        return false;
      }
      const slug = String(getTranslation(locale).slug || '').trim();
      return Boolean(slug);
    });

    const openWebsitePreview = () => {
      if (!cmsActivityId.value || !previewToken.value) {
        showToast('error', t('cms.activities.previewUnavailable'));
        return;
      }
      if (!websiteSiteUrl.value) {
        showToast('error', t('cms.activities.previewMissingSiteUrl'));
        return;
      }

      const locale = String(currentLanguage.value || 'en').toLowerCase();
      if (locale !== 'en' && locale !== 'nl') {
        showToast('error', t('cms.activities.previewLocaleUnsupported'));
        return;
      }

      const slug = String(getTranslation(locale).slug || '').trim();
      if (!slug) {
        showToast('error', t('cms.activities.previewMissingSlug'));
        return;
      }

      const base = websiteSiteUrl.value.replace(/\/+$/, '');
      const pathPrefix = locale === 'nl' ? '/nl' : '';
      const url = `${base}${pathPrefix}/activities/${encodeURIComponent(slug)}?preview_token=${encodeURIComponent(previewToken.value)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Translation keyword management
    const saveCmsActivity = async () => {
      if (!selectedActivity.value || !agencySlug.value) {
        showToast("error", "Please select an activity first");
        return;
      }

      if (!validateTranslations()) {
        return;
      }

      try {
        await persistActivityConfiguration();
        const cmsData = buildCmsActivityData();
        let successMessage = "";

        if (cmsActivityId.value) {
          // Update existing CMS activity
          await api.activityService.updateCmsActivity(agencySlug.value, cmsActivityId.value, cmsData);
          await persistPendingMediaChanges({ showToast: false });
          successMessage = "CMS Activity updated successfully";
        } else {
          // Create new CMS activity
          const response = await api.activityService.createCmsActivity(agencySlug.value, cmsData);
          // Handle different response structures
          if (response?.id) {
            cmsActivityId.value = response.id;
          } else if (response?.data?.id) {
            cmsActivityId.value = response.data.id;
          } else if (response?.data?.data?.id) {
            cmsActivityId.value = response.data.data.id;
          }
          capturePreviewToken(extractCmsActivityData(response) || response?.data || response);
          await persistPendingMediaChanges({ showToast: false });
          successMessage = "CMS Activity created successfully";
        }

        showToast("success", successMessage);
      } catch (error) {
        console.error("Error saving CMS activity:", error);
        showApiError(error, "Error saving CMS activity. Please try again.");
      }
    };

    const guiderOptions = computed(() => guiders.value);
    const selectedGuiders = computed(() => {
      return guiders.value.filter((g: any) => form.guiders.includes(g.value));
    });

    const reduceGuiderOption = (option: any) => {
      return typeof option === 'object' && option.value !== undefined ? option.value : option;
    };

    const loadGuiders = async () => {
      if (!agencySlug.value) return;
      isLoadingGuiders.value = true;
      try {
        const response = await api.guiderService.listCmsGuiders(agencySlug.value, {
          perPage: 100,
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        const currentLocale = (currentLanguage.value || 'en').toLowerCase();
        guiders.value = list.map((item: any) => {
          const normalized = normalizeResource(item) || item;
          const translations = extractListData(
            extractRelationship(normalized, 'translations') ||
              normalized.translations ||
              normalized.attributes?.translations ||
              []
          );
          const translation =
            translations.find((t: any) => (t?.locale || '').toLowerCase() === currentLocale) ||
            translations[0] ||
            {};

          // Extract user name
          const agencyUserRelation = normalizeResource(extractRelationship(normalized, 'agencyUser')) || normalized.agencyUser || normalized.agency_user || {};
          const userName =
            agencyUserRelation?.user?.name ||
            '';

          // Prefer guider title in dropdown, then fall back to related user name.
          const guiderTitle = translation?.title || normalized.title || normalized.name || '';
          const displayLabel = guiderTitle || userName || `#${normalized.id}`;

          return {
            value: normalized.id,
            label: displayLabel,
            id: normalized.id,
            title: guiderTitle,
            tagline: translation?.tagline || normalized.tagline || '',
            userName,
          };
        });
      } catch (error) {
        console.error('Failed to load guiders', error);
      } finally {
        isLoadingGuiders.value = false;
      }
    };

    const getCategoryMediaCollectionName = (item: any) => {
      if (!item) return "";
      return item.collection_name || item.collectionName || item.attributes?.collection_name || "";
    };

    const getCategoryMediaUrl = (item: any) => {
      if (!item) return "";
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.url ||
        attributes.url ||
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        ""
      );
    };

    const loadCategories = async () => {
      if (!agencySlug.value) return;
      isLoadingCategories.value = true;
      try {
        const response = await api.activityCategoryService.listCmsActivityCategories(agencySlug.value, {
          filters: {},
        });
        
        const list = extractListData(response);
        const currentLocale = currentLanguage.value || 'en';
        
        availableCategories.value = list.map((cat: any) => {
          const normalized = normalizeResource(cat) || cat;
          const translations = extractListData(normalized.translations || normalized.attributes?.translations || []);
          
          // Find translation for current locale, fallback to first available
          const translation = translations.find((t: any) => 
            (t.locale || '').toLowerCase() === currentLocale.toLowerCase()
          ) || translations[0] || {};

          const mediaRelationship = extractRelationship(cat, "media");
          const normalizedMedia = normalizeResource(mediaRelationship);
          const mediaItems = Array.isArray(normalizedMedia)
            ? normalizedMedia
            : normalizedMedia
              ? [normalizedMedia]
              : [];
          const iconMedia = mediaItems.find((media: any) => {
            const normalizedMediaItem = normalizeResource(media) || media;
            return getCategoryMediaCollectionName(normalizedMediaItem) === "cms_activity_category_icon";
          });
          const icon = iconMedia ? getCategoryMediaUrl(iconMedia) : "";
          
          return {
            id: normalized.id || normalized.attributes?.id,
            name: translation.name || normalized.attributes?.name || normalized.name || '',
            slug: normalized.slug || normalized.attributes?.slug || '',
            icon,
            order: normalized.order || normalized.attributes?.order || 0,
            is_public: normalized.is_public ?? normalized.attributes?.is_public ?? true,
          };
        }).sort((a: any, b: any) => a.order - b.order);
      } catch (error) {
        console.error('Failed to load categories', error);
        showApiError(error, 'Failed to load categories');
      } finally {
        isLoadingCategories.value = false;
      }
    };

    watch(locales, (localeList) => {
      if (!localeList.length) {
        return;
      }
      const resolved = resolveStoredLocale(currentLanguage.value || localStorage.getItem('language'));
      if (resolved) {
        currentLanguage.value = resolved;
      }
      if (!localeList.includes(normalizeLocaleKey(faqTabLanguage.value))) {
        faqTabLanguage.value = resolved || localeList[0];
      }
    }, { immediate: true });

    watch(currentLanguage, () => {
      if (selectedActivity.value && agencySlug.value) {
        void loadCategories();
      }
    });

    watch(
      linkedActivityModality,
      (m) => {
        if (m === "group" && (!form.date_ranges || form.date_ranges.length === 0)) {
          form.date_ranges = [createEmptyTripDateRange()];
        }
        if (m === "self-guided") {
          form.date_ranges = [];
          form.start_date = "";
        }
      }
    );

    const handleCategoryCreated = async () => {
      // Reload categories when a new one is created
      await loadCategories();
    };

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const removeGuider = (guiderId: number) => {
      const index = form.guiders.indexOf(guiderId);
      if (index > -1) {
        form.guiders.splice(index, 1);
      }
    };
    const removeReview = (index: number) => form.reviews.splice(index, 1);

    const isLoadingFaqs = ref(false);
    const faqIdsToDelete = ref<number[]>([]);
    const expandedFaqs = ref<Record<number, boolean>>({});

    const faqLocales = computed(() =>
      languages.value?.length
        ? languages.value.map((lang: { shortName?: string; key?: string }) =>
            (lang.shortName || lang.key || '').toLowerCase(),
          )
        : getAppLanguageLocales(concepts.concepts.value?.['app.languages']),
    );
    const faqLanguages = computed(() => {
      if (languages.value?.length) {
        return languages.value;
      }
      return [];
    });

    watch(
      faqLocales,
      () => {
        initOverviewFaqs();
      },
      { immediate: true },
    );

    const getFaqTranslation = (faqItem: any, locale: string) => {
      if (!faqItem.translations) return { question: '', answer: '', section_label: '', sub_label: '', items: [] };
      let t = faqItem.translations.find((x: any) => (x.locale || '').toLowerCase() === locale.toLowerCase());
      if (!t) {
        t = { locale, question: '', answer: '', section_label: '', sub_label: '', items: [] };
        faqItem.translations.push(t);
      }
      if (!Array.isArray(t.items)) {
        t.items = [];
      }
      return t;
    };

    const getFaqPreviewQuestion = (faqItem: any) => {
      const locale = faqTabLanguage.value || 'en';
      const t = getFaqTranslation(faqItem, locale);
      if (t && t.question) return t.question;
      const first = (faqItem.translations || []).find((x: any) => x.question);
      return first ? first.question : '';
    };

    const getOverviewFaqTranslation = (placement: OverviewPlacementKey, locale: string) => {
      if (!overviewFaqs.value[placement]) {
        overviewFaqs.value[placement] = createEmptyOverviewFaq(placement);
      }
      return getFaqTranslation(overviewFaqs.value[placement], locale);
    };

    const importFromActivityBuilder = async () => {
      if (!selectedActivity.value || !agencySlug.value) {
        return;
      }

      isImportingStages.value = true;
      try {
        const daysData = await api.activityService.retrieveActivityDays(
          agencySlug.value,
          selectedActivity.value.id,
        );
        const days = Array.isArray(daysData) ? daysData : (daysData ? [daysData] : []);

        const locationMap: Record<number, string> = {};
        try {
          const allLocations = await api.location.all();
          allLocations.forEach((loc: any) => {
            if (loc?.id) {
              locationMap[loc.id] = loc.name || '';
            }
          });
        } catch (error) {
          console.warn('Could not load locations for stage import', error);
        }

        form.stages = mapActivityDaysToStages(days, selectedActivity.value, languages.value, locationMap);
        expandedDays.value = form.stages.length > 0 ? { [form.stages[0].id]: true } : {};
      } catch (error) {
        console.error('Error importing stages from Activity Builder:', error);
        showApiError(error, 'Failed to import itinerary from Activity Builder.');
      } finally {
        isImportingStages.value = false;
      }
    };

    const openCopyFromActivityModal = (activityId: number | null) => {
      if (!activityId) {
        copySourceActivityId.value = null;
        return;
      }
      copySourceActivityId.value = activityId;
      showCopyModal.value = true;
    };

    const closeCopyModal = () => {
      showCopyModal.value = false;
      copySourceActivityId.value = null;
    };

    const applyOverviewFaqsFromSource = (sourceFaqs: FaqItem[]) => {
      const overviewPlacementKeys = OVERVIEW_FAQ_PLACEMENTS.map((p) => p.key);
      const nextOverviewFaqs = {} as Record<OverviewPlacementKey, FaqItem>;

      OVERVIEW_FAQ_PLACEMENTS.forEach(({ key }) => {
        nextOverviewFaqs[key] = createEmptyOverviewFaq(key);
      });

      sourceFaqs.forEach((faqItem) => {
        const placement = faqItem.placement || 'things_to_know';
        if (overviewPlacementKeys.includes(placement as OverviewPlacementKey)) {
          nextOverviewFaqs[placement as OverviewPlacementKey] = {
            ...faqItem,
            id: undefined,
            content_type: faqItem.content_type || 'prose',
          };
        }
      });

      overviewFaqs.value = nextOverviewFaqs;
    };

    const applyCopyFromActivity = async () => {
      if (!copySourceActivityId.value || !agencySlug.value || copySections.value.length === 0) {
        return;
      }

      isCopyingActivity.value = true;
      try {
        const response = await api.activityService.getCmsActivity(agencySlug.value, copySourceActivityId.value);
        let sourceData = extractCmsActivityData(response);
        if (!sourceData) {
          sourceData = normalizeCmsActivity(response?.data || response);
        }
        if (!sourceData) {
          showToast('error', t('general.errorMessage'));
          return;
        }

        if (copySections.value.includes('translations')) {
          const translationsSource = extractRelationship(sourceData, 'translations');
          const translationsList = normalizeResource(translationsSource) || [];
          if (Array.isArray(translationsList)) {
            translationsList.forEach((translation: any) => {
              const normalizedTranslation = normalizeResource(translation) || translation;
              const locale = (normalizedTranslation.locale || '').toLowerCase();
              if (!locale) return;
              const target = getTranslation(locale);
              target.title = normalizedTranslation.title || target.title;
              target.content = normalizedTranslation.content || target.content;
              target.slug = normalizedTranslation.slug || target.slug;
              target.seo_title = normalizedTranslation.seo_title || normalizedTranslation.seoTitle || target.seo_title;
              target.seo_description =
                normalizedTranslation.seo_description || normalizedTranslation.seoDescription || target.seo_description;
              target.keywords = Array.isArray(normalizedTranslation.keywords)
                ? normalizedTranslation.keywords
                : target.keywords;
              target.featured_image_alt =
                normalizedTranslation.featured_image_alt || normalizedTranslation.featuredImageAlt || target.featured_image_alt;
            });
            translationFieldsKey.value += 1;
          }
        }

        if (copySections.value.includes('configuration')) {
          const activity = normalizeResource(sourceData) || sourceData;
          form.duration = activity.duration ?? form.duration;
          form.general_country = activity.general_country || activity.generalCountry || form.general_country;
          form.general_accomodation = activity.general_accomodation || activity.generalAccomodation || form.general_accomodation;
          form.difficulty = activity.difficulty || form.difficulty;
          form.type_of_booking = activity.type_of_booking || activity.typeOfBooking || form.type_of_booking;
          form.map = activity.map || form.map;
          form.available_languages = Array.isArray(activity.available_languages || activity.availableLanguages)
            ? (activity.available_languages || activity.availableLanguages)
            : form.available_languages;
          form.transfer = activity.transfer ?? form.transfer;
          form.transport = activity.transport ?? form.transport;
        }

        if (copySections.value.includes('stages')) {
          const stagesRelation = extractRelationship(sourceData, 'stages');
          const normalizedStages = normalizeResource(stagesRelation);
          if (Array.isArray(normalizedStages) && normalizedStages.length > 0) {
            const currentLocale = currentLanguage.value || 'en';
            form.stages = normalizedStages.map((stage: any, index: number) => {
              const normalizedStage = normalizeResource(stage) || stage;
              const stageTranslations = extractRelationship(normalizedStage, 'translations');
              const normalizedTranslations = normalizeResource(stageTranslations);
              const translationsList = Array.isArray(normalizedTranslations) ? normalizedTranslations : [];
              const currentTranslation =
                translationsList.find((tr: any) => (normalizeResource(tr)?.locale || tr?.locale) === currentLocale) ||
                translationsList[0] ||
                null;
              const translationData = normalizeResource(currentTranslation) || currentTranslation || {};
              const title = translationData.title || normalizedStage.title || '';
              const description = translationData.description || normalizedStage.description || '';
              const allTranslations: StageTranslation[] = languages.value.map((lang: any) => {
                const locale = lang.shortName || lang.locale || lang;
                const translation = translationsList.find((tr: any) => {
                  const tLocale = normalizeResource(tr)?.locale || tr?.locale;
                  return tLocale === locale;
                });
                const tData = normalizeResource(translation) || translation || {};
                return {
                  locale,
                  title: tData.title || (locale === currentLocale ? title : ''),
                  description: tData.description || (locale === currentLocale ? description : ''),
                  featured_image_alt: tData.featured_image_alt ?? tData.featuredImageAlt ?? '',
                  from: tData.from || '',
                  to: tData.to || '',
                  location_label: tData.location_label ?? tData.locationLabel ?? '',
                };
              });

              return {
                id: `stage-copy-${Date.now()}-${index}`,
                order: normalizedStage.order || index + 1,
                title,
                description,
                translations: allTranslations,
                extra_data: normalizedStage.extra_data || [],
                featured_image: '',
                gallery_text: normalizedStage.gallery_text || '',
                is_stage: Boolean(normalizedStage.is_stage ?? normalizedStage.isStage ?? false),
                mode: normalizedStage.mode || null,
                distance_km: normalizedStage.distance_km ?? normalizedStage.distanceKm ?? null,
              };
            });
          }
        }

        if (copySections.value.includes('categories')) {
          const categoriesRelation = extractRelationship(sourceData, 'categories');
          const normalizedCategories = normalizeResource(categoriesRelation);
          if (Array.isArray(normalizedCategories)) {
            form.categories = normalizedCategories
              .map((category: any) => (normalizeResource(category) || category)?.id)
              .filter((id: any): id is number => typeof id === 'number' && id > 0);
          }
        }

        if (copySections.value.includes('suggestedActivities')) {
          const suggestedRelation = extractRelationship(sourceData, 'suggestedActivities');
          const normalizedSuggested = normalizeResource(suggestedRelation);
          if (Array.isArray(normalizedSuggested)) {
            form.suggested_activities = normalizedSuggested
              .map((item: any) => (normalizeResource(item) || item)?.id)
              .filter((id: any): id is number => typeof id === 'number' && id > 0);
          }
        }

        if (copySections.value.includes('overview') || copySections.value.includes('faq')) {
          const faqResponse = await api.faqService.listCmsFaqs(agencySlug.value, {
            filters: { cms_activity_id: copySourceActivityId.value },
            perPage: 1000,
          });
          const sourceFaqs = extractListData(faqResponse).map((faq: any) => {
            const normalized = normalizeResource(faq) || faq;
            const apiTranslations = normalizeResource(extractRelationship(faq, 'translations')) || [];
            const translations = faqLocales.value.map((locale) => {
              const existing = apiTranslations.find((t: any) => (t.locale || '').toLowerCase() === locale);
              return {
                locale,
                question: existing?.question ?? '',
                answer: existing?.answer ?? '',
              };
            });
            return {
              id: undefined,
              order: normalized.order || 0,
              is_public: true,
              placement: normalized.placement || 'things_to_know',
              content_type: normalized.content_type || normalized.contentType,
              translations,
            } as FaqItem;
          });

          if (copySections.value.includes('overview')) {
            applyOverviewFaqsFromSource(sourceFaqs);
          }

          if (copySections.value.includes('faq')) {
            form.faq = sourceFaqs
              .filter((faqItem) => (faqItem.placement || 'things_to_know') === 'things_to_know')
              .map((faqItem) => ({
                ...faqItem,
                placement: 'things_to_know',
                content_type: 'accordion',
              }));
          }
        }

        showToast('success', t('cms.activities.applyCopy'));
        closeCopyModal();
      } catch (error) {
        console.error('Error copying CMS activity:', error);
        showApiError(error, 'Failed to copy from CMS activity.');
      } finally {
        isCopyingActivity.value = false;
      }
    };

    const loadFaqs = async () => {
      if (!cmsActivityId.value || !agencySlug.value) {
        form.faq = [];
        expandedFaqs.value = {};
        return;
      }

      isLoadingFaqs.value = true;
      try {
        const response = await api.faqService.listCmsFaqs(agencySlug.value, {
          filters: {
            cms_activity_id: cmsActivityId.value,
          },
          perPage: 1000, // Get all FAQs for this activity
        });

        const faqsData = extractListData(response);
        const overviewPlacementKeys = OVERVIEW_FAQ_PLACEMENTS.map((p) => p.key);
        const thingsToKnowFaqs: FaqItem[] = [];
        const nextOverviewFaqs = {} as Record<OverviewPlacementKey, FaqItem>;

        OVERVIEW_FAQ_PLACEMENTS.forEach(({ key }) => {
          nextOverviewFaqs[key] = createEmptyOverviewFaq(key);
        });

        faqsData.forEach((faq: any) => {
          const normalized = normalizeResource(faq) || faq;
          const placement = normalized.placement || 'things_to_know';
          const apiTranslations = normalizeResource(extractRelationship(faq, 'translations')) || [];
          const translations = faqLocales.value.map((locale) => {
            const existing = apiTranslations.find((t: any) => (t.locale || '').toLowerCase() === locale);
            return {
              locale,
              question: existing?.question ?? existing?.title ?? '',
              answer: existing?.answer ?? '',
              section_label: existing?.section_label ?? existing?.sectionLabel ?? '',
              sub_label: existing?.sub_label ?? existing?.subLabel ?? '',
              items: Array.isArray(existing?.items) ? existing.items : [],
            };
          });
          const mappedFaq: FaqItem = {
            id: normalized.id,
            order: normalized.order || 0,
            is_public: true,
            placement,
            content_type: normalized.content_type || normalized.contentType,
            translations,
          };

          if (overviewPlacementKeys.includes(placement as OverviewPlacementKey)) {
            nextOverviewFaqs[placement as OverviewPlacementKey] = {
              ...mappedFaq,
              content_type: mappedFaq.content_type || 'prose',
            };
          } else {
            thingsToKnowFaqs.push({
              ...mappedFaq,
              placement: 'things_to_know',
              content_type: mappedFaq.content_type || 'accordion',
            });
          }
        });

        form.faq = thingsToKnowFaqs.sort((a, b) => (a.order || 0) - (b.order || 0));
        overviewFaqs.value = nextOverviewFaqs;
        expandedFaqs.value = {};
      } catch (error) {
        console.error('Error loading FAQs:', error);
        form.faq = [];
        overviewFaqs.value = {} as Record<OverviewPlacementKey, FaqItem>;
        initOverviewFaqs();
        expandedFaqs.value = {};
      } finally {
        isLoadingFaqs.value = false;
      }
    };

    const saveFaqItem = async (faqItem: FaqItem, order: number, placement: string, contentType: string) => {
      const isListContent = contentType === 'list';
      const translations = (faqItem.translations || [])
        .map((tr: any) => {
          const question = (tr.question || '').trim();
          const answer = (tr.answer || '').trim();
          const sectionLabel = (tr.section_label || '').trim();
          const subLabel = (tr.sub_label || '').trim();
          const items = Array.isArray(tr.items)
            ? tr.items.map((item: string) => (item || '').trim()).filter(Boolean)
            : [];

          const hasContent = isListContent
            ? question || sectionLabel || subLabel || items.length > 0
            : question || answer;

          if (!hasContent) {
            return null;
          }

          return {
            locale: tr.locale,
            question,
            answer: isListContent ? '' : answer,
            section_label: sectionLabel || null,
            title: question || null,
            sub_label: subLabel || null,
            items: isListContent ? items : null,
          };
        })
        .filter(Boolean);

      if (translations.length === 0) {
        if (faqItem.id) {
          const deleteList = placement === 'things_to_know' ? faqIdsToDelete : overviewFaqIdsToDelete;
          deleteList.value.push(faqItem.id);
        }
        return;
      }

      const faqData = {
        cms_activity_id: cmsActivityId.value,
        order,
        is_public: true,
        placement,
        content_type: contentType,
        translations,
      };

      if (faqItem.id) {
        const response = await api.faqService.updateCmsFaq(agencySlug.value!, faqItem.id, faqData);
        const saved = normalizeResource(response) || response?.data || response;
        if (saved?.id) {
          faqItem.id = saved.id;
        }
        return saved;
      }

      const response = await api.faqService.createCmsFaq(agencySlug.value!, faqData);
      const saved = normalizeResource(response) || response?.data || response;
      if (saved?.id) {
        faqItem.id = saved.id;
      }
      return saved;
    };

    const saveFaqs = async () => {
      if (!cmsActivityId.value || !agencySlug.value) {
        return;
      }

      // Delete removed FAQs
      for (const faqId of faqIdsToDelete.value) {
        await api.faqService.deleteCmsFaq(agencySlug.value, faqId);
      }
      faqIdsToDelete.value = [];

      for (const faqId of overviewFaqIdsToDelete.value) {
        await api.faqService.deleteCmsFaq(agencySlug.value, faqId);
      }
      overviewFaqIdsToDelete.value = [];

      for (let index = 0; index < form.faq.length; index++) {
        const faqItem = form.faq[index];
        await saveFaqItem(faqItem, index, 'things_to_know', 'accordion');
      }

      let overviewOrder = 0;
      for (const { key, contentType } of OVERVIEW_FAQ_PLACEMENTS) {
        const faqItem = overviewFaqs.value[key];
        if (!faqItem) continue;
        await saveFaqItem(faqItem, overviewOrder, key, contentType);
        overviewOrder += 1;
      }

      await loadFaqs();
    };

    const addFaqItem = () => {
      const newIndex = form.faq.length;
      form.faq.push({
        id: undefined,
        order: newIndex,
        is_public: true,
        placement: 'things_to_know',
        content_type: 'accordion',
        translations: faqLocales.value.map((locale) => ({
          locale,
          question: '',
          answer: '',
        })),
      });
      expandedFaqs.value[newIndex] = true;
    };

    const toggleFaqAccordion = (index: number) => {
      expandedFaqs.value[index] = !expandedFaqs.value[index];
    };

    const removeFaqItem = (index: number) => {
      const faqItem = form.faq[index];
      if (faqItem.id) {
        faqIdsToDelete.value.push(faqItem.id);
      }
      form.faq.splice(index, 1);
      
      // Rebuild expandedFaqs object to match new indices
      const newExpandedFaqs: Record<number, boolean> = {};
      form.faq.forEach((_, newIndex) => {
        if (newIndex < index && expandedFaqs.value[newIndex]) {
          newExpandedFaqs[newIndex] = true;
        } else if (newIndex >= index && expandedFaqs.value[newIndex + 1]) {
          newExpandedFaqs[newIndex] = true;
        }
      });
      expandedFaqs.value = newExpandedFaqs;
    };

    // Itinerary stages (days) accordion state and methods
    const expandedDays = ref<Record<string, boolean>>({});

    const isDayExpanded = (stageId: string) => !!expandedDays.value[stageId];

    const toggleDay = (stageId: string) => {
      expandedDays.value = {
        ...expandedDays.value,
        [stageId]: !expandedDays.value[stageId],
      };
    };

    const allStagesExpanded = computed(() => {
      if (!form.stages.length) {
        return false;
      }
      return form.stages.every((stage) => expandedDays.value[stage.id]);
    });

    const totalStageDistanceKm = computed(() => {
      let sum = 0;
      let hasAny = false;
      form.stages.forEach((stage) => {
        if (!stage.is_stage || stage.distance_km == null) {
          return;
        }
        const distance = Number(stage.distance_km);
        if (!Number.isNaN(distance)) {
          sum += distance;
          hasAny = true;
        }
      });
      return hasAny ? sum : null;
    });

    const expandAllStages = () => {
      const next: Record<string, boolean> = {};
      form.stages.forEach((stage) => {
        next[stage.id] = true;
      });
      expandedDays.value = next;
    };

    const collapseAllStages = () => {
      expandedDays.value = {};
    };

    const toggleAllStages = () => {
      if (allStagesExpanded.value) {
        collapseAllStages();
        return;
      }
      expandAllStages();
    };

    const formatStageOrder = (order: number) => String(order).padStart(2, '0');

    const formatStageDaysSummary = (count: number) => {
      const key = count === 1 ? 'cms.activities.stageDayCountOne' : 'cms.activities.stageDayCountMany';
      return t(key, { count });
    };

    const parseCmsStageId = (stage: Stage): number | null => {
      if (typeof stage.cmsStageId === 'number' && stage.cmsStageId > 0) {
        return stage.cmsStageId;
      }
      return null;
    };

    const getStageInitialMedia = (stage: Stage) => stageInitialMedia.value[stage.id] || [];

    const getStageRouteSummary = (stage: Stage) => {
      const translation = getStageTranslation(stage, currentLanguage.value);
      if (!stage.is_stage) {
        return translation.location_label?.trim() || '';
      }
      const from = translation.from?.trim();
      const to = translation.to?.trim();
      if (!from && !to) {
        return '';
      }
      return `${from || '…'} → ${to || '…'}`;
    };

    const parseStageDistance = (value: string | number | null | undefined): number | null => {
      if (value === '' || value == null) {
        return null;
      }
      const num = Number.parseFloat(String(value));
      return Number.isNaN(num) ? null : num;
    };

    const formatStageDistance = (value: number | null | undefined): string => {
      if (value == null || Number.isNaN(Number(value))) {
        return '';
      }
      const num = Number(value);
      if (Number.isInteger(num)) {
        return String(num);
      }
      return num.toFixed(2).replace(/\.?0+$/, '');
    };

    const getStageMetaLine = (stage: Stage) => {
      if (!stage.is_stage || !stage.mode) {
        return '';
      }
      return stageModeOptions.value[stage.mode as keyof typeof stageModeOptions.value] || stage.mode;
    };

    const addStage = () => {
      const newOrder = form.stages.length > 0 
        ? Math.max(...form.stages.map((s) => s.order)) + 1 
        : 1;
      
      // Initialize translations for all languages
      const translations: StageTranslation[] = languages.value.map((lang: any) => ({
        locale: lang.shortName || lang.locale || lang,
        title: '',
        description: '',
        featured_image_alt: '',
        from: '',
        to: '',
        location_label: '',
      }));
      
      const newStage: Stage = {
        id: `stage-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
        order: newOrder,
        title: '',
        description: '',
        translations: translations,
        extra_data: [],
        featured_image: '',
        gallery_text: '',
        is_stage: true,
        mode: null,
        distance_km: null,
      };
      form.stages.push(newStage);
      expandedDays.value = { ...expandedDays.value, [newStage.id]: true };
      // Ensure stages are sorted by order
      form.stages.sort((a, b) => a.order - b.order);
    };

    const removeStage = (index: number) => {
      const stage = form.stages[index];
      if (stage) {
        const nextExpanded = { ...expandedDays.value };
        delete nextExpanded[stage.id];
        expandedDays.value = nextExpanded;
        // Store the old order before removal
        const removedOrder = stage.order;
        // Remove from array
        form.stages.splice(index, 1);
        // Reorder remaining stages
        form.stages.forEach((s, i) => {
          s.order = i + 1;
        });
        // Update media payloads: stages after the removed one need their orders updated
        // Create a new map with updated orders
        const updatedMediaPayloads = new Map<number, any[]>();
        for (const [order, media] of stageMediaPayloads.value.entries()) {
          if (order < removedOrder) {
            // Stages before removed stage keep their order
            updatedMediaPayloads.set(order, media);
          } else if (order > removedOrder) {
            // Stages after removed stage get order decremented by 1
            updatedMediaPayloads.set(order - 1, media);
          }
          // Order matching removedOrder is discarded (that stage's media)
        }
        stageMediaPayloads.value = updatedMediaPayloads;
      }
    };

    const getStageTranslation = (stage: Stage, locale: string): StageTranslation => {
      if (!stage.translations || !Array.isArray(stage.translations)) {
        // Initialize translations if they don't exist
        stage.translations = languages.value.map((lang: any) => ({
          locale: lang.shortName || lang.locale || lang,
          title: '',
          description: '',
          featured_image_alt: '',
          from: '',
          to: '',
          location_label: '',
        }));
      }
      
      let translation = stage.translations.find(
        (t: StageTranslation) => normalizeLocaleKey(t.locale) === normalizeLocaleKey(locale),
      );
      if (!translation) {
        // Create translation if it doesn't exist
        translation = {
          locale: normalizeLocaleKey(locale),
          title: '',
          description: '',
          featured_image_alt: '',
          from: '',
          to: '',
          location_label: '',
        };
        stage.translations.push(translation);
      }
      
      return translation;
    };

    const setStageMode = (stage: Stage, value: string | number | boolean | null) => {
      stage.mode = value == null || value === '' ? null : String(value);
    };

    const updateStageTranslation = (
      stage: Stage,
      locale: string,
      field: 'title' | 'description' | 'featured_image_alt' | 'from' | 'to' | 'location_label',
      value: string,
    ) => {
      const translation = getStageTranslation(stage, locale);
      if (field === 'title') {
        translation.title = value;
      } else if (field === 'description') {
        translation.description = value;
      } else if (field === 'from') {
        translation.from = value;
      } else if (field === 'to') {
        translation.to = value;
      } else if (field === 'location_label') {
        translation.location_label = value;
      } else {
        translation.featured_image_alt = value;
      }

      // Also update stage.title/description for current language to keep them in sync
      if (normalizeLocaleKey(locale) === normalizeLocaleKey(currentLanguage.value)) {
        if (field === 'title') {
          stage.title = value;
        } else if (field === 'description') {
          stage.description = value;
        }
      }
    };

    // Store stage media uploads by stage order (not ID) since stages don't have DB IDs until after creation
    // Key: stage order (number), Value: media array from upload API
    const stageMediaPayloads = ref<Map<number, any[]>>(new Map());
    const STAGE_MEDIA_MODEL_TYPE = 'cms_activity_stage';
    const STAGE_MEDIA_COLLECTION = 'cms_activity_stage_featured';

    const confirmStageMedia = async () => {
      if (!cmsActivityId.value || stageMediaPayloads.value.size === 0 || !agencySlug.value) {
        return;
      }

      try {
        const response = await api.activityService.getDetailedCmsActivityByActivityId(
          agencySlug.value,
          getBaseActivityId()!
        );
        const cmsActivityData = extractCmsActivityData(response);
        
        if (!cmsActivityData) {
          return;
        }

        const stagesRelation = extractRelationship(cmsActivityData, 'stages');
        const normalizedStages = normalizeResource(stagesRelation);
        const apiStages = Array.isArray(normalizedStages) ? normalizedStages : [];

        for (const [stageOrder, mediaArray] of stageMediaPayloads.value.entries()) {
          if (!hasPendingCmsMediaSave(mediaArray, STAGE_MEDIA_COLLECTION)) {
            stageMediaPayloads.value.delete(stageOrder);
            continue;
          }

          const apiStage = apiStages.find((as: any) => {
            const normalized = normalizeResource(as) || as;
            return normalized.order === stageOrder;
          });

          if (!apiStage) {
            console.warn(`Stage with order ${stageOrder} not found in API response`);
            continue;
          }

          const normalizedApiStage = normalizeResource(apiStage) || apiStage;
          const stageId = normalizedApiStage.id;

          if (stageId && typeof stageId === 'number') {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value,
              mediaItems: mediaArray,
              modelType: STAGE_MEDIA_MODEL_TYPE,
              modelId: stageId,
              collection: STAGE_MEDIA_COLLECTION,
            });
            stageMediaPayloads.value.delete(stageOrder);
          } else {
            console.warn(`Stage with order ${stageOrder} has invalid ID: ${stageId}`);
          }
        }
      } catch (error) {
        console.error('Error confirming stage media:', error);
      }
    };

    const handleStageImageUpload = async (stageId: string, media: any[]) => {
      if (!media || (Array.isArray(media) ? media.length === 0 : !media)) {
        return;
      }

      const mediaArray = Array.isArray(media) ? media : [media];
      const mediaUrl = mediaArray[0]?.original_url || mediaArray[0]?.preview_url || mediaArray[0]?.url || '';

      const stage = form.stages.find((s) => s.id === stageId);
      if (!stage) {
        return;
      }

      try {
        if (mediaUrl) {
          stage.featured_image = mediaUrl;
        }

        stageMediaPayloads.value.set(stage.order, mediaArray);

        if (cmsActivityId.value && agencySlug.value) {
          try {
            const response = await api.activityService.getDetailedCmsActivityByActivityId(
              agencySlug.value,
              getBaseActivityId()!
            );
            const cmsActivityData = extractCmsActivityData(response);
            
            if (cmsActivityData) {
              const stagesRelation = extractRelationship(cmsActivityData, 'stages');
              const normalizedStages = normalizeResource(stagesRelation);
              const apiStages = Array.isArray(normalizedStages) ? normalizedStages : [];
              
              const apiStage = apiStages.find((as: any) => {
                const normalized = normalizeResource(as) || as;
                return normalized.order === stage.order;
              });

              if (apiStage) {
                const normalizedApiStage = normalizeResource(apiStage) || apiStage;
                const dbStageId = normalizedApiStage.id;

                if (dbStageId && typeof dbStageId === 'number' && hasPendingCmsMediaSave(mediaArray, STAGE_MEDIA_COLLECTION)) {
                  await persistCmsMediaAttachment({
                    api,
                    driver: MEDIA_DRIVER,
                    agencySlug: agencySlug.value,
                    mediaItems: mediaArray,
                    modelType: STAGE_MEDIA_MODEL_TYPE,
                    modelId: dbStageId,
                    collection: STAGE_MEDIA_COLLECTION,
                  });
                  stageMediaPayloads.value.delete(stage.order);
                  stage.cmsStageId = dbStageId;
                  return;
                }
              }
            }
          } catch (confirmError) {
            console.warn('Could not confirm stage media immediately, will confirm after save:', confirmError);
          }
        }
      } catch (error) {
        console.error('Error handling stage image upload:', error);
        showApiError(error);
      }
    };

    // Watch for stages changes to initialize expanded state
    watch(
      () => form.stages,
      (newStages) => {
        if (newStages.length > 0) {
          const hasExpanded = Object.values(expandedDays.value).some(Boolean);
          if (!hasExpanded && newStages[0]) {
            expandedDays.value = { [newStages[0].id]: true };
          }
          const stageIds = new Set(newStages.map((s) => s.id));
          const nextExpanded = { ...expandedDays.value };
          let changed = false;
          Object.keys(nextExpanded).forEach((id) => {
            if (!stageIds.has(id)) {
              delete nextExpanded[id];
              changed = true;
            }
          });
          if (changed) {
            expandedDays.value = nextExpanded;
          }
        } else {
          expandedDays.value = {};
        }
      },
      { immediate: true, deep: true }
    );

    // Watch for language changes to update stage title/description
    watch(
      currentLanguage,
      (newLocale, oldLocale) => {
        if (form.stages.length > 0 && languages.value.length > 0 && oldLocale) {
          const oldLocaleKey = normalizeLocaleKey(oldLocale);
          const newLocaleKey = normalizeLocaleKey(newLocale);

          form.stages.forEach((stage) => {
            if (oldLocaleKey && stage.translations) {
              const oldTranslation = stage.translations.find(
                (t: StageTranslation) => normalizeLocaleKey(t.locale) === oldLocaleKey,
              );
              if (oldTranslation) {
                oldTranslation.title = stage.title || '';
                oldTranslation.description = stage.description || '';
              } else {
                stage.translations.push({
                  locale: oldLocaleKey,
                  title: stage.title || '',
                  description: stage.description || '',
                  featured_image_alt: '',
                  from: '',
                  to: '',
                  location_label: '',
                });
              }
            }

            if (stage.translations && Array.isArray(stage.translations)) {
              const translation = stage.translations.find(
                (t: StageTranslation) => normalizeLocaleKey(t.locale) === newLocaleKey,
              );
              if (translation) {
                stage.title = translation.title || '';
                stage.description = translation.description || '';
              } else {
                stage.title = '';
                stage.description = '';
                stage.translations.push({
                  locale: newLocaleKey,
                  title: '',
                  description: '',
                  featured_image_alt: '',
                  from: '',
                  to: '',
                  location_label: '',
                });
              }
            } else {
              stage.translations = [{
                locale: newLocaleKey,
                title: stage.title || '',
                description: stage.description || '',
                featured_image_alt: '',
                from: '',
                to: '',
                location_label: '',
              }];
              stage.title = '';
              stage.description = '';
            }
          });
        }
      }
    );

    const togglePublishState = async () => {
      if (!agencySlug.value || !cmsActivityId.value) {
        showToast("error", t("general.errorMessage"));
        return;
      }

      publishProcessing.value = true;
      const nextState = !form.is_public;

      try {
        await api.activityService.publishCmsActivity(agencySlug.value, cmsActivityId.value, nextState);
        form.is_public = nextState;
        showToast("success", nextState ? t("cms.activities.publishSuccess") : t("cms.activities.unpublishSuccess"));
      } catch (error) {
        console.error("Error updating publish state:", error);
        showApiError(error);
      } finally {
        publishProcessing.value = false;
      }
    };

    // Navigation and save functions
    const goBack = () => {
      router.push({ name: "cms.activities" });
    };

    const handleSubmit = () => {
      saveAndPublish();
    };

    const buildActivityConfigurationData = () => {
      if (!selectedActivity.value?.id) {
        throw new Error("No activity selected");
      }

      return {
        transfer: form.transfer,
        transport: form.transport,
      };
    };

    const persistActivityConfiguration = async () => {
      if (!agencySlug.value || !selectedActivity.value?.id) {
        return;
      }

      const payload = buildActivityConfigurationData();
      await api.activityService.updateActivity(agencySlug.value, selectedActivity.value.id, payload);
    };

    const buildCmsActivityData = (isDraft = false) => {
      if (!selectedActivity.value || !agencySlug.value) {
        throw new Error("No activity selected");
      }

      const baseData: any = {
        activity_id: selectedActivity.value.id,
        is_public: form.is_public,
        is_draft: isDraft,
        duration: String(form.duration || ""),
        general_country: form.general_country,
        general_accomodation: form.general_accomodation,
        difficulty: form.difficulty ? form.difficulty : null,
        categories: form.categories.filter((cat: number | string) => cat).map((cat: number | string) => {
          // Ensure categories are sent as IDs (numbers)
          return typeof cat === 'number' ? cat : parseInt(cat as string, 10);
        }).filter((id: number) => !isNaN(id)),
        available_languages: form.available_languages.filter((lang: string) => lang),
        type_of_booking: form.type_of_booking,
        map: form.map,
        is_featured: form.is_featured,
        translations: form.translations.map((translation: Translation) => ({
          locale: translation.locale,
          title: translation.title,
          content: translation.content,
          slug: translation.slug,
          seo_title: translation.seo_title,
          seo_description: translation.seo_description,
          keywords: translation.keywords.filter((kw: string) => kw),
          featured_image_alt: translation.featured_image_alt || '',
        })),
        guiders: form.guiders,
        suggested_activities: form.suggested_activities.filter((id) => typeof id === 'number' && id > 0),
        stages: form.stages.map((stage) => {
          const currentLocaleKey = normalizeLocaleKey(currentLanguage.value || 'en');
          const languageList = languages.value?.length
            ? languages.value
            : (stage.translations || []).map((t: StageTranslation) => ({ shortName: t.locale }));
          const stageTranslations = languageList.map((lang: any) => {
            const locale = normalizeLocaleKey(lang.shortName || lang.locale || lang);
            const isCurrentLocale = locale === currentLocaleKey;

            if (isCurrentLocale) {
              const curT = getStageTranslation(stage, locale);
              return {
                locale,
                title: stage.title || curT.title || '',
                description: stage.description || curT.description || '',
                featured_image_alt: curT.featured_image_alt || '',
                from: curT.from || '',
                to: curT.to || '',
                location_label: curT.location_label || '',
              };
            }

            const existingTranslation = stage.translations?.find(
              (t: StageTranslation) => normalizeLocaleKey(t.locale) === locale,
            );
            return {
              locale,
              title: existingTranslation?.title || '',
              description: existingTranslation?.description || '',
              featured_image_alt: existingTranslation?.featured_image_alt || '',
              from: existingTranslation?.from || '',
              to: existingTranslation?.to || '',
              location_label: existingTranslation?.location_label || '',
            };
          });

          // Ensure at least one translation exists so publish validation accepts stages
          if (stageTranslations.length === 0) {
            const curT = getStageTranslation(stage, currentLocaleKey);
            stageTranslations.push({
              locale: currentLocaleKey,
              title: stage.title || curT.title || '',
              description: stage.description || curT.description || '',
              featured_image_alt: curT.featured_image_alt || '',
              from: curT.from || '',
              to: curT.to || '',
              location_label: curT.location_label || '',
            });
          }

          const cmsStageId = parseCmsStageId(stage);

          return {
            ...(cmsStageId ? { id: cmsStageId } : {}),
            order: stage.order,
            is_stage: stage.is_stage ?? false,
            mode: stage.is_stage ? (stage.mode || null) : null,
            distance_km: stage.is_stage ? (stage.distance_km ?? null) : null,
            translations: stageTranslations,
          };
        }),
      };

      // Add price fields based on modality
      if (isGroupModality.value) {
        const ranges = form.date_ranges
          .map((r) => ({
            start_date: r.start_date || null,
            end_date: r.end_date || null,
            base_price_single_room: Number(r.base_price_single_room) || 0,
            base_price_double_room: Number(r.base_price_double_room) || 0,
            max_pax: Number(r.max_pax) || 0,
            guider_ids: Array.isArray(r.guider_ids)
              ? r.guider_ids.filter((id) => typeof id === 'number' && id > 0)
              : [],
          }))
          .filter((r) => r.start_date && r.end_date);
        baseData.date_ranges = ranges;
        baseData.start_date = ranges[0]?.start_date ?? null;
        baseData.base_price_single_room = form.base_price_single_room ?? 0;
        baseData.base_price_double_room = form.base_price_double_room ?? 0;
        const allGuiderIds = [
          ...new Set(ranges.flatMap((r) => r.guider_ids ?? [])),
        ];
        baseData.guiders = allGuiderIds;
      } else {
        baseData.base_price = form.base_price || 0;
        baseData.start_date = null;
        baseData.date_ranges = null;
      }

      return baseData;
    };

    const saveDraft = async () => {
      if (!selectedActivity.value || !agencySlug.value) {
        showToast("error", "Please select an activity first");
        return;
      }

      // Soft-save: skip publish validation, but keep the current publish status.
      // Forcing is_public=false here unpublished activities and hid itineraries on the website.
      try {
        await persistActivityConfiguration();
        const cmsData = buildCmsActivityData(true);
        const successMessage = form.is_public
          ? "Changes saved successfully"
          : "Draft saved successfully";

        if (cmsActivityId.value) {
          // Update existing CMS activity
          await api.activityService.updateCmsActivity(agencySlug.value, cmsActivityId.value, cmsData);
        } else {
          // Create new CMS activity
          const response = await api.activityService.createCmsActivity(agencySlug.value, cmsData);
          // Handle different response structures
          if (response?.id) {
            cmsActivityId.value = response.id;
          } else if (response?.data?.id) {
            cmsActivityId.value = response.data.id;
          } else if (response?.data?.data?.id) {
            cmsActivityId.value = response.data.data.id;
          }
          capturePreviewToken(extractCmsActivityData(response) || response?.data || response);
        }

        await persistPendingMediaChanges({ showToast: false });

        // Confirm stage media after activity/stages are saved
        await confirmStageMedia();
        await reloadCmsActivityFromApi();

        // Save FAQs after activity is saved
        await saveFaqs();

        showToast("success", successMessage);
        // goBack();
      } catch (error) {
        console.error("Error saving draft:", error);
        showApiError(error, "Error saving draft. Please try again.");
      }
    };

    const saveAndPublish = async () => {
      if (!selectedActivity.value || !agencySlug.value) {
        showToast("error", "Please select an activity first");
        return;
      }

      if (!validateTranslations()) {
        return;
      }

      form.is_public = true;

      try {
        await persistActivityConfiguration();
        const cmsData = buildCmsActivityData();

        if (cmsActivityId.value) {
          // Update existing CMS activity
          await api.activityService.updateCmsActivity(agencySlug.value, cmsActivityId.value, cmsData);
          await persistPendingMediaChanges({ showToast: false });
          // Confirm stage media after activity/stages are saved
          await confirmStageMedia();
          await reloadCmsActivityFromApi();
          // Save FAQs after activity is saved
          await saveFaqs();
          // Publish it
          await api.activityService.publishCmsActivity(agencySlug.value, cmsActivityId.value, true);
        } else {
          // Create new CMS activity
          const response = await api.activityService.createCmsActivity(agencySlug.value, cmsData);
          // Handle different response structures
          let newCmsActivityId = null;
          if (response?.id) {
            newCmsActivityId = response.id;
          } else if (response?.data?.id) {
            newCmsActivityId = response.data.id;
          } else if (response?.data?.data?.id) {
            newCmsActivityId = response.data.data.id;
          }
          cmsActivityId.value = newCmsActivityId;
          await persistPendingMediaChanges({ showToast: false });
          // Confirm stage media after activity/stages are created
          await confirmStageMedia();
          await reloadCmsActivityFromApi();
          // Save FAQs after activity is created
          if (newCmsActivityId) {
            await saveFaqs();
            await api.activityService.publishCmsActivity(agencySlug.value, newCmsActivityId, true);
          }
        }

        showToast("success", "Activity published successfully");
        // goBack();
      } catch (error) {
        console.error("Error saving and publishing:", error);
        showApiError(error, "Error saving and publishing. Please try again.");
      }
    };

    const canSave = computed(
      () =>
        !!selectedActivity.value &&
        translationsAreComplete.value &&
        !isLoadingActivity.value &&
        !isLoadingCmsActivity.value
    );

    const CMS_MEDIA_MODEL_TYPE = "cms_activity";
    const FEATURED_MEDIA_COLLECTION = "cms_activity_featured";
    const GALLERY_MEDIA_COLLECTION = "cms_activity_gallery";
    const MEDIA_DRIVER = "s3";

    const featuredMediaPayload = ref<any[] | null>(null);
    const galleryMediaPayload = ref<any[] | null>(null);
    const pendingFeaturedDeletion = ref<any | null>(null);
    const pendingGalleryDeletionIds = ref<number[]>([]);

    const cloneMediaItems = (items: any[] = []) => {
      if (!Array.isArray(items)) {
        return [];
      }
      return items.map((item) => JSON.parse(JSON.stringify(item)));
    };

    const getCollectionName = (item: any) => {
      if (!item) return "";
      return item.collection_name || item.collectionName || "";
    };

    const getCustomProperties = (item: any) => {
      if (!item) return {};
      return item.custom_properties || item.customProperties || {};
    };

    const getOrderColumn = (item: any) => {
      if (!item) return undefined;
      return item.order_column ?? item.orderColumn ?? undefined;
    };

    const getMediaUrl = (item: any) => {
      if (!item) return "";

      const attributes = item.attributes || {};

      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.url ||
        attributes.url ||
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        ""
      );
    };

    const getMediaPreviewUrl = (item: any, fallback: string) => {
      const attributes = item?.attributes || {};
      return item.preview_url || item.previewUrl || attributes.preview_url || attributes.previewUrl || fallback;
    };

    const normalizeMediaForUploader = (item: any, fallbackOrder = 1) => {
      if (!item) {
        return null;
      }

      const customProps = getCustomProperties(item);
      const order = customProps?.order ?? getOrderColumn(item) ?? fallbackOrder;
      const storageUrl = getMediaUrl(item);
      const previewUrl = getMediaPreviewUrl(item, storageUrl);

      return {
        ...item,
        order,
        order_column: order,
        original_url: storageUrl,
        originalUrl: storageUrl,
        storage_url: storageUrl,
        storageUrl: storageUrl,
        preview_url: previewUrl,
        previewUrl: previewUrl,
        attributes: {
          ...(item.attributes || {}),
          uuid: item.uuid,
          name: item.name || `media-${order}`,
          order_column: order,
          collection_name: getCollectionName(item),
          original_url: storageUrl,
          originalUrl: storageUrl,
          storage_url: storageUrl,
          storageUrl: storageUrl,
          preview_url: previewUrl,
          previewUrl: previewUrl,
          isExisting: true,
          urls: {
            ...(item.attributes?.urls || {}),
            original: storageUrl,
            preview: previewUrl,
          },
        },
      };
    };

    const extractStageFeaturedMedia = (normalizedStage: any) => {
      const stageMedia = extractRelationship(normalizedStage, 'media');
      const normalizedMedia = normalizeResource(stageMedia);
      const mediaArray = Array.isArray(normalizedMedia) ? normalizedMedia : [];

      const featuredItems = mediaArray
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => getCollectionName(item) === STAGE_MEDIA_COLLECTION)
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      const featuredUrl = featuredItems.length > 0 ? getMediaUrl(featuredItems[0]) : '';
      return { featuredUrl, featuredItems };
    };

    const extractMediaItems = (cmsActivity: any) => {
      const mediaRelationship = extractRelationship(cmsActivity, "media");
      const normalizedRelationship = normalizeResource(mediaRelationship);

      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }

      if (mediaRelationship?.data && Array.isArray(mediaRelationship.data)) {
        return mediaRelationship.data;
      }

      if (Array.isArray(cmsActivity.media)) {
        return cmsActivity.media;
      }

      return [];
    };

    const mapActivityMediaCollections = (cmsActivity: any) => {
      const mediaItems = extractMediaItems(cmsActivity)
        .map((item: any) => normalizeResource(item) || item)
        .filter((item: any) => !!item && !!item.uuid);

      const featuredItems = mediaItems
        .filter((item: any) => getCollectionName(item) === "cms_activity_featured")
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean);

      const galleryItems = mediaItems
        .filter((item: any) => getCollectionName(item) === "cms_activity_gallery")
        .map((item: any, index: number) => normalizeMediaForUploader(item, index + 1))
        .filter(Boolean)
        .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

      featuredUploaderInitialMedia.value = cloneMediaItems(featuredItems);
      galleryUploaderInitialMedia.value = cloneMediaItems(galleryItems);

      pendingFeaturedDeletion.value = null;
      pendingGalleryDeletionIds.value = [];

      const featuredUrl = getMediaUrl(featuredItems[0]);
      if (featuredUrl) {
        form.featured_image = featuredUrl;
      }

      const galleryUrls = galleryItems.map((item: any) => getMediaUrl(item)).filter((url: string) => !!url);

      if (galleryUrls.length > 0) {
        form.gallery = galleryUrls;
      } else {
        form.gallery = [];
      }
    };

    const getBaseActivityId = () =>
      selectedActivity.value?.id ?? selectedActivity.value?.activity_id ?? activity.value?.id ?? null;

    const deleteMediaItemsByIds = async (ids: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(new Set((ids || []).filter((id): id is number => typeof id === "number" && id > 0)));

      if (!uniqueIds.length) {
        return;
      }

      await Promise.all(uniqueIds.map((id) => api.media.delete(MEDIA_DRIVER, id)));
    };

    const refreshCmsActivityMediaState = async () => {
      if (!agencySlug.value || !cmsActivityId.value) {
        return;
      }

      const baseActivityId = getBaseActivityId();
      if (!baseActivityId) {
        return;
      }

      try {
        const response = await api.activityService.getDetailedCmsActivityByActivityId(agencySlug.value, baseActivityId);
        const cmsActivityData = extractCmsActivityData(response);
        if (cmsActivityData) {
          mapActivityMediaCollections(cmsActivityData);
        }
      } catch (error) {
        console.error("Error refreshing CMS activity media:", error);
      }
    };

    const reloadCmsActivityFromApi = async () => {
      if (!agencySlug.value || !cmsActivityId.value) {
        return;
      }

      try {
        const response = await api.activityService.getCmsActivity(agencySlug.value, cmsActivityId.value);
        let cmsActivityData = extractCmsActivityData(response);
        if (!cmsActivityData) {
          cmsActivityData = normalizeCmsActivity(response?.data || response);
        }
        if (cmsActivityData) {
          capturePreviewToken(cmsActivityData);
          mapCmsActivityDataToForm(cmsActivityData);
        }
      } catch (error) {
        console.error('Error reloading CMS activity:', error);
      }
    };

    const isSyncingFeaturedMedia = ref(false);
    const isSyncingGalleryMedia = ref(false);

    const confirmFeaturedMedia = async ({ showToast: shouldToast = true }: { showToast?: boolean } = {}) => {
      if (
        !cmsActivityId.value ||
        isSyncingFeaturedMedia.value ||
        isLoadingActivity.value ||
        isLoadingCmsActivity.value
      ) {
        return;
      }

      const payload = featuredMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, FEATURED_MEDIA_COLLECTION);
      const hasPendingDeletion = !!pendingFeaturedDeletion.value?.id;

      if (!hasUploads && !hasPendingDeletion) {
        return;
      }

      try {
        await runCmsMediaSync(isSyncingFeaturedMedia, async () => {
          if (hasPendingDeletion) {
            await deleteMediaItemsByIds([pendingFeaturedDeletion.value?.id]);
            pendingFeaturedDeletion.value = null;
          } else if (hasUploads && featuredUploaderInitialMedia.value.length > 0) {
            await deleteMediaItemsByIds(featuredUploaderInitialMedia.value.map((item: any) => item.id));
          }

          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: cmsActivityId.value!,
              collection: FEATURED_MEDIA_COLLECTION,
            });
          }

          featuredMediaPayload.value = null;
          featuredImageUploadRef.value?.clearMedia?.();
          await refreshCmsActivityMediaState();
        });

        if (shouldToast) {
          showToast("success", t("cms.activities.featuredMediaSaveSuccess"));
        }
      } catch (error) {
        console.error("Error saving featured media:", error);
        showApiError(error);
      }
    };

    const confirmGalleryMedia = async ({ showToast: shouldToast = true }: { showToast?: boolean } = {}) => {
      if (
        !cmsActivityId.value ||
        isSyncingGalleryMedia.value ||
        isLoadingActivity.value ||
        isLoadingCmsActivity.value
      ) {
        return;
      }

      const payload = galleryMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, GALLERY_MEDIA_COLLECTION);
      const hasDeletions = pendingGalleryDeletionIds.value.length > 0;

      if (!hasUploads && !hasDeletions) {
        return;
      }

      try {
        await runCmsMediaSync(isSyncingGalleryMedia, async () => {
          if (hasDeletions) {
            await deleteMediaItemsByIds(pendingGalleryDeletionIds.value);
            pendingGalleryDeletionIds.value = [];
          }

          if (hasUploads) {
            await persistCmsMediaAttachment({
              api,
              driver: MEDIA_DRIVER,
              agencySlug: agencySlug.value!,
              mediaItems: payload,
              modelType: CMS_MEDIA_MODEL_TYPE,
              modelId: cmsActivityId.value!,
              collection: GALLERY_MEDIA_COLLECTION,
            });
          }

          galleryMediaPayload.value = null;
          galleryUploadRef.value?.clearMedia?.();
          await refreshCmsActivityMediaState();
        });

        if (shouldToast) {
          showToast("success", t("cms.activities.galleryMediaSaveSuccess"));
        }
      } catch (error) {
        console.error("Error saving gallery media:", error);
        showApiError(error);
      }
    };

    const persistPendingMediaChanges = async ({ showToast: shouldToast = false }: { showToast?: boolean } = {}) => {
      await Promise.all([
        confirmFeaturedMedia({ showToast: shouldToast }),
        confirmGalleryMedia({ showToast: shouldToast }),
      ]);
    };

    const handleFeaturedUploaded = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleFeaturedRemoved = (media: any[] = []) => {
      featuredMediaPayload.value = Array.isArray(media) ? [...media] : [];
      if ((!media || media.length === 0) && featuredUploaderInitialMedia.value.length > 0) {
        pendingFeaturedDeletion.value = featuredUploaderInitialMedia.value[0];
      }
    };

    const handleGalleryUploaded = (media: any[] = []) => {
      galleryMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };

    const handleGalleryRemoved = (media: any[] = []) => {
      const normalized = Array.isArray(media) ? [...media] : [];
      galleryMediaPayload.value = normalized;

      if (galleryUploaderInitialMedia.value.length === 0) {
        return;
      }

      const currentIds = new Set(
        normalized
          .map((item: any) => (typeof item?.id === "number" ? item.id : null))
          .filter((id): id is number => typeof id === "number")
      );

      const removedItems = galleryUploaderInitialMedia.value.filter(
        (item: any) => typeof item?.id === "number" && !currentIds.has(item.id)
      );

      if (removedItems.length > 0) {
        const ids = removedItems.map((item: any) => item.id).filter((id: any): id is number => typeof id === "number");
        pendingGalleryDeletionIds.value = Array.from(new Set([...pendingGalleryDeletionIds.value, ...ids]));
        galleryUploaderInitialMedia.value = galleryUploaderInitialMedia.value.filter(
          (item: any) => !ids.includes(item.id)
        );
      }
    };

    const canSaveFeaturedMedia = computed(
      () =>
        !!cmsActivityId.value &&
        featuredMediaPayload.value !== null &&
        !isSyncingFeaturedMedia.value &&
        !isLoadingActivity.value &&
        !isLoadingCmsActivity.value
    );
    const canSaveGalleryMedia = computed(
      () =>
        !!cmsActivityId.value &&
        galleryMediaPayload.value !== null &&
        !isSyncingGalleryMedia.value &&
        !isLoadingActivity.value &&
        !isLoadingCmsActivity.value
    );

    const saveFeaturedMedia = async () => {
      await confirmFeaturedMedia();
    };

    const saveGalleryMedia = async () => {
      await confirmGalleryMedia();
    };

    // Computed properties for optional price fields
    const basePriceSingleRoom = computed({
      get: () => form.base_price_single_room ?? 0,
      set: (value: number) => {
        form.base_price_single_room = value;
      },
    });

    const basePriceDoubleRoom = computed({
      get: () => form.base_price_double_room ?? 0,
      set: (value: number) => {
        form.base_price_double_room = value;
      },
    });

    return {
      isEditing,
      form,
      activity,
      activities,
      selectedActivity,
      isFormFieldsDisabled,
      isLoadingActivity,
      isLoadingCmsActivity,
      translationFieldsKey,
      modalityOptions,
      linkedActivityModality,
      isGroupModality,
      linkedActivityModalityLabel,
      bookingTypeOptions,
      difficultyOptions,
      languageOptions,
      isLanguageSelected,
      toggleLanguage,
      availableCategories,
      isLoadingCategories,
      showCategoryModal,
      loadCategories,
      handleCategoryCreated,
      onSearch,
      selectActivity,
      clearActivity,
      formatDate,
      languages,
      currentLanguage,
      faqTabLanguage,
      getTranslation,
      saveCmsActivity,
      removeGuider,
      removeReview,
      guiders,
      guiderOptions,
      selectedGuiders,
      isLoadingGuiders,
      reduceGuiderOption,
      addFaqItem,
      removeFaqItem,
      toggleFaqAccordion,
      expandedFaqs,
      isLoadingFaqs,
      loadFaqs,
      faqLanguages,
      getFaqTranslation,
      getFaqPreviewQuestion,
      expandedDays,
      isDayExpanded,
      toggleDay,
      allStagesExpanded,
      totalStageDistanceKm,
      toggleAllStages,
      formatStageOrder,
      formatStageDaysSummary,
      formatStageDistance,
      getStageRouteSummary,
      getStageMetaLine,
      parseStageDistance,
      addStage,
      removeStage,
      getStageTranslation,
      updateStageTranslation,
      setStageMode,
      handleStageImageUpload,
      getStageInitialMedia,
      goBack,
      handleSubmit,
      saveDraft,
      saveAndPublish,
      featuredImageUploadRef,
      galleryUploadRef,
      togglePublishState,
      publishProcessing,
      cmsActivityId,
      translationsAreComplete,
      canSave,
      canSaveFeaturedMedia,
      canSaveGalleryMedia,
      isSyncingFeaturedMedia,
      isSyncingGalleryMedia,
      saveFeaturedMedia,
      saveGalleryMedia,
      handleFeaturedUploaded,
      handleFeaturedRemoved,
      handleGalleryUploaded,
      handleGalleryRemoved,
      featuredUploaderInitialMedia,
      galleryUploaderInitialMedia,
      basePriceSingleRoom,
      basePriceDoubleRoom,
      activitySetupRoute,
      canPreviewOnWebsite,
      openWebsitePreview,
      cmsActivityOptions,
      reduceActivityOption,
      overviewPlacements,
      getOverviewFaqTranslation,
      stageModeOptions,
      importFromActivityBuilder,
      isImportingStages,
      copySourceActivityId,
      showCopyModal,
      copySections,
      copySectionOptions,
      openCopyFromActivityModal,
      closeCopyModal,
      applyCopyFromActivity,
      isCopyingActivity,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/components/cms/cms-styles.scss";

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-activity-form {
  min-height: 100vh;
  color: $cms-text-primary;
}

.cms-form-header-actions .cms-btn,
.cms-form-header-link {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-sm;
  text-decoration: none;
}

.cms-service-label-with-tooltip {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-xs;
}

.cms-service-tooltip {
  display: inline-flex;
  opacity: 0.7;
  cursor: help;
}

.cms-itinerary-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $cms-spacing-md;
  flex-wrap: wrap;
  width: 100%;

  &__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $cms-spacing-sm;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $cms-spacing-sm;
    margin-left: auto;
  }
}

.cms-itinerary-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: $cms-font-size-sm;
  font-weight: 500;
  color: $cms-text-secondary;
  background: $cms-background-gray-medium;
  border: 1px solid $cms-border-color;

  &--accent {
    color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.08);
    border-color: rgba($cms-primary-color, 0.2);
    font-weight: 600;
  }
}

.cms-itinerary-count {
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  padding: 0.25rem 0.625rem;
  background: $cms-background-gray-medium;
  border-radius: $cms-radius-sm;
  margin-right: auto;
}

.cms-stage-type-toggle {
  margin: $cms-spacing-sm 0;
}

.cms-overview-cards {
  display: grid;
  gap: $cms-spacing-lg;
  margin-top: $cms-spacing-lg;
}

.cms-overview-card {
  padding: $cms-spacing-md;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;
  background: $cms-background-gray-medium;
}

.cms-overview-card-title {
  margin: 0 0 $cms-spacing-md;
  font-size: $cms-font-size-md;
  font-weight: 600;
}

.cms-copy-from-activity {
  margin-top: $cms-spacing-md;
}

.cms-copy-sections {
  display: grid;
  gap: $cms-spacing-sm;
  margin: $cms-spacing-md 0;
}

.cms-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  padding: $cms-spacing-lg;
}

.cms-modal {
  width: 100%;
  max-width: 560px;
  background: white;
  border-radius: $cms-radius-lg;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.cms-modal-large {
  max-width: 720px;
}

.cms-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $cms-spacing-lg;
  border-bottom: 1px solid $cms-border-color;

  h3 {
    margin: 0;
    font-size: $cms-font-size-lg;
  }
}

.cms-modal-close-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: $cms-spacing-xs;
}

.cms-modal-body {
  padding: $cms-spacing-lg;
}

.cms-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: $cms-spacing-md;
  margin-top: $cms-spacing-lg;
}

// Activity selection section
.retrieve-activity {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cms-selected-activity {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: $cms-success-light;
  border-radius: 8px;
  border: 1px solid $cms-success-color;

  h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: $cms-success-dark;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: $cms-success-color;
  }
}

.cms-loading-activity {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: $cms-background-gray-medium;
  border-radius: 8px;
  border: 1px solid $cms-border-color-gray;

  p {
    margin: 0;
    font-size: 0.9rem;
    color: $cms-text-muted-dark;
  }
}

// CMS Activity Fields Section (unique to this view)
.cms-wp-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid $cms-border-color;
}

.cms-wp-section-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  color: $cms-text-primary;
}

.cms-wp-section-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

// Form grid for CMS fields (unique to this view)
.cms-form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  :deep(.cms-form-group-full) {
    grid-column: 1 / -1;
  }
}


// Configuration section grid (two columns)
.cms-configuration-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  // Map wrapper is a grid item; nested .cms-form-group-full does not span this grid
  > .cms-map-services-group {
    grid-column: 1 / -1;
  }

  // Elements with full-width should span both columns
  :deep(.cms-form-group-full) {
    grid-column: 1 / -1;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;

    > .cms-map-services-group {
      grid-column: 1;
    }

    :deep(.cms-form-group-full) {
      grid-column: 1;
    }
  }
}

.cms-readonly-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.875rem 1rem;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;
  background: $cms-background-white;
}

.cms-readonly-field__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: $cms-text-secondary;
}

.cms-readonly-field__value {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: $cms-text-primary;
}

.cms-available-languages {
  padding: 0.875rem;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;
  background: $cms-background-white;
}

.cms-language-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  margin-top: 0.625rem;
}

.cms-language-option {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid $cms-border-color;
  border-radius: 999px;
  background: $cms-background-light;
  cursor: pointer;
  transition: all $cms-transition-base;

  .cms-checkbox {
    margin: 0;
  }

  .cms-checkbox-label {
    color: $cms-text-primary;
    font-size: $cms-font-size-sm;
    font-weight: 500;
  }

  &:hover {
    border-color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.06);
  }

  &.is-selected {
    border-color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.12);
    box-shadow: inset 0 0 0 1px rgba($cms-primary-color, 0.18);
  }
}

.cms-map-services-group {
  :deep(.cms-form-group) {
    margin-bottom: 0;
  }
}

.cms-services-card {
  padding: 0.875rem;
  border: 1px solid $cms-border-color-dark;
  border-radius: $cms-radius-md;
  background: $cms-background-white;
}

.cms-service-options {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  margin-top: 0.625rem;
}

.cms-service-option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-sm;
  background: #fff;
  cursor: pointer;
  transition: all $cms-transition-base;

  .cms-checkbox {
    margin: 0;
  }

  .cms-checkbox-label {
    color: $cms-text-primary;
    font-weight: 500;
  }

  &:hover {
    border-color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.06);
  }

  &.is-selected {
    border-color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.12);
    box-shadow: inset 0 0 0 1px rgba($cms-primary-color, 0.18);
  }
}

// Form actions (unique to this view)
.cms-form-actions {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid $cms-border-color;
  display: flex;
  justify-content: flex-end;
  grid-column: 1 / -1;
}

// Stages styling (unique to this view)
.cms-itinerary-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cms-itinerary-day-row {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr);
  gap: $cms-spacing-md;

  &.is-last .cms-itinerary-connector {
    display: none;
  }
}

.cms-itinerary-spine {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1.35rem;
}

.cms-itinerary-node {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 2px solid $cms-border-color;
  background: $cms-background-white;
  color: $cms-text-secondary;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
  transition: all $cms-transition-base;

  &.is-expanded {
    border-color: $cms-primary-color;
    background: $cms-primary-color;
    color: white;
    box-shadow: 0 0 0 4px rgba($cms-primary-color, 0.12);
  }
}

.cms-itinerary-connector {
  flex: 1;
  width: 2px;
  min-height: 1.5rem;
  margin: 0.35rem 0;
  background: linear-gradient(to bottom, rgba($cms-primary-color, 0.35), $cms-border-color);
  border-radius: 1px;
}

.cms-itinerary-days {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-sm;
}

.cms-itinerary-day {
  background: $cms-background-white;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-lg;
  overflow: hidden;
  transition: border-color $cms-transition-base, box-shadow $cms-transition-base;
  margin-bottom: $cms-spacing-md;

  &:hover {
    border-color: rgba($cms-primary-color, 0.45);
  }

  &.is-expanded {
    border-color: $cms-primary-color;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);

    .cms-itinerary-day-toggle-icon {
      transform: rotate(0deg);
    }

    .cms-itinerary-day-header {
      background: rgba($cms-primary-color, 0.04);
      border-bottom: 1px solid $cms-border-color;
    }
  }
}

.cms-itinerary-day-header {
  display: flex;
  align-items: stretch;
  gap: $cms-spacing-sm;
  background: $cms-background-gray-light;
  transition: background-color $cms-transition-base;
}

.cms-itinerary-day-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.75rem;
  border: none;
  background: transparent;
  cursor: pointer;
  color: $cms-text-secondary;
  transition: background-color $cms-transition-base, color $cms-transition-base;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: $cms-primary-color;
  }
}

.cms-itinerary-day-toggle-icon {
  transition: transform $cms-transition-base;
  transform: rotate(-90deg);
}

.cms-itinerary-day-thumb {
  flex-shrink: 0;
  width: 3.5rem;
  height: 3.5rem;
  margin: $cms-spacing-sm 0;
  border-radius: $cms-radius-md;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid $cms-border-color;
  background: $cms-background-gray-medium;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.cms-itinerary-day-header-main {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  flex: 1;
  min-width: 0;
  padding: $cms-spacing-md $cms-spacing-sm $cms-spacing-md 0;
  cursor: pointer;
}

.cms-itinerary-day-header-aside {
  display: flex;
  align-items: center;
  gap: $cms-spacing-sm;
  padding: $cms-spacing-sm $cms-spacing-md $cms-spacing-sm 0;
  flex-shrink: 0;
}

.cms-itinerary-distance-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 0.15rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: rgba($cms-primary-color, 0.1);
  border: 1px solid rgba($cms-primary-color, 0.2);
  white-space: nowrap;

  &__value {
    font-size: $cms-font-size-md;
    font-weight: 700;
    color: $cms-primary-color;
    line-height: 1;
  }

  &__unit {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: rgba($cms-primary-color, 0.75);
    letter-spacing: 0.03em;
  }
}

.cms-itinerary-day-badge {
  flex-shrink: 0;
  min-width: 3.5rem;
  padding: 0.35rem 0.65rem;
  border-radius: $cms-radius-sm;
  background: $cms-primary-color;
  color: white;
  font-size: $cms-font-size-sm;
  font-weight: 600;
  text-align: center;
}

.cms-itinerary-day-summary {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cms-itinerary-day-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.cms-itinerary-day-label {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $cms-text-secondary;
}

.cms-itinerary-day-title-input {
  flex: 1;
  min-width: 10rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid transparent;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-md;
  font-weight: 600;
  background: transparent;
  color: $cms-text-primary;
  transition: all $cms-transition-base;

  &:hover,
  &:focus {
    outline: none;
    background: $cms-background-white;
    border-color: $cms-border-color;
  }

  &:focus {
    border-color: $cms-primary-color;
    box-shadow: 0 0 0 2px rgba($cms-primary-color, 0.12);
  }

  &::placeholder {
    color: $cms-text-secondary;
    font-weight: 400;
  }
}

.cms-itinerary-day-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-itinerary-day-type {
  display: inline-flex;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;

  &.is-route {
    background: rgba($cms-primary-color, 0.12);
    color: $cms-primary-color;
  }

  &.is-location {
    background: $cms-background-gray-medium;
    color: $cms-text-secondary;
  }
}

.cms-itinerary-day-route {
  font-weight: 500;
  color: $cms-text-primary;
}

.cms-itinerary-day-meta-line {
  color: $cms-text-secondary;
}

.cms-itinerary-day-distance {
  color: $cms-text-secondary;
}

.cms-itinerary-day-header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.cms-itinerary-day-content {
  background: $cms-background-white;
}

.cms-itinerary-day-body {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-lg;
  padding: $cms-spacing-lg;
}

.cms-itinerary-panel {
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;
  background: $cms-background-gray-light;
  overflow: hidden;

  &--route {
    background: $cms-background-white;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $cms-spacing-md;
    flex-wrap: wrap;
    padding: $cms-spacing-md $cms-spacing-lg;
    border-bottom: 1px solid $cms-border-color;
    background: rgba(0, 0, 0, 0.02);
  }

  &__title {
    margin: 0;
    font-size: $cms-font-size-sm;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $cms-text-secondary;
  }

  &__body {
    padding: $cms-spacing-lg;

    &--media {
      display: grid;
      grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
      gap: $cms-spacing-lg;

      @media (max-width: 900px) {
        grid-template-columns: 1fr;
      }
    }
  }
}

.cms-itinerary-day-type-switch {
  display: inline-flex;
  padding: 0.2rem;
  border-radius: 999px;
  background: $cms-background-gray-medium;
  border: 1px solid $cms-border-color;

  &__btn {
    border: none;
    background: transparent;
    padding: 0.35rem 0.85rem;
    border-radius: 999px;
    font-size: $cms-font-size-sm;
    font-weight: 600;
    color: $cms-text-secondary;
    cursor: pointer;
    transition: all $cms-transition-base;

    &:hover:not(:disabled) {
      color: $cms-text-primary;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    &.is-active {
      background: $cms-background-white;
      color: $cms-primary-color;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    }
  }
}

.cms-itinerary-day-image {
  min-width: 0;
}

.cms-itinerary-day-fields-panel {
  min-width: 0;
}

.cms-itinerary-day-fields {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
}

.cms-itinerary-route-fields {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
}

.cms-itinerary-route-preview {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: $cms-radius-sm;
  background: rgba($cms-primary-color, 0.06);
  border: 1px dashed rgba($cms-primary-color, 0.25);
  font-size: $cms-font-size-sm;
  font-weight: 600;
  color: $cms-text-primary;

  &__arrow {
    color: $cms-primary-color;
    font-weight: 400;
  }
}

.cms-itinerary-route-card {
  padding: $cms-spacing-md;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;
  background: $cms-background-gray-light;
}

.cms-itinerary-route-card-header {
  margin-bottom: $cms-spacing-md;
}

.cms-itinerary-route-toggle {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-sm;
  font-weight: 600;
  cursor: pointer;
}

.cms-itinerary-route-hint {
  margin: 0 0 $cms-spacing-md;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
}

.cms-itinerary-route-grid {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
}

.cms-itinerary-route-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $cms-spacing-md;
  align-items: start;

  &--stacked {
    grid-template-columns: 1fr;
  }

  :deep(.cms-form-group) {
    margin-bottom: 0;
    min-width: 0;
  }

  :deep(.cms-input-label-wrapper),
  :deep(.cms-form-group > label) {
    display: flex;
    align-items: center;
    min-height: 1.25rem;
    margin-bottom: 0.2rem;
  }

  :deep(.cms-input),
  :deep(.cms-select) {
    height: 2rem;
    min-height: 2rem;
    box-sizing: border-box;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}

.cms-itinerary-actions {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-top: $cms-spacing-md;
  margin-top: $cms-spacing-sm;
  border-top: 1px solid $cms-border-color;
}

.cms-itinerary-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $cms-spacing-lg;
}

.cms-itinerary-empty-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: $cms-spacing-sm;
}

// Legacy stage styles (for backwards compatibility)
.cms-stage-item {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid $cms-border-color;
}

.cms-stage-header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid $cms-border-color;

  h4 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: $cms-text-primary;
  }
}

// FAQ section styles (unique to this view)
.cms-faq-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: $cms-background-gray-medium;
  border-radius: $cms-radius-lg;
  border: 2px dashed $cms-border-color;
  color: $cms-text-secondary;

  .v-icon {
    color: $cms-text-muted;
    margin-bottom: $cms-spacing-md;
  }

  p {
    margin: 0.5rem 0;
    font-size: $cms-font-size-base;
    color: $cms-text-secondary;
  }

  .cms-faq-empty-hint {
    font-size: $cms-font-size-sm;
    color: $cms-text-muted;
  }
}

.cms-faq-list {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-lg;
  margin-bottom: $cms-spacing-lg;
}

.cms-faq-item-wrapper {
  background: white;
  border-radius: $cms-radius-lg;
  border: 1px solid $cms-border-color;
  overflow: hidden;
  transition: all $cms-transition-base;
  box-shadow: $cms-shadow-sm;

  &:hover {
    box-shadow: $cms-shadow-md;
    border-color: $cms-primary-color;
  }

  .cms-faq-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $cms-spacing-md $cms-spacing-lg;
    background: linear-gradient(135deg, $cms-background-gray-light 0%, $cms-background-gray-medium 100%);
    border-bottom: 1px solid $cms-border-color;
    cursor: pointer;
    user-select: none;
    transition: background-color $cms-transition-fast;

    &:hover {
      background: linear-gradient(135deg, $cms-background-gray-medium 0%, $cms-background-gray-light 100%);
    }

    .cms-faq-item-number {
      display: flex;
      align-items: center;
      gap: $cms-spacing-sm;
      font-weight: 600;
      font-size: $cms-font-size-base;
      color: $cms-text-primary;
      flex: 1;
      min-width: 0;

      .cms-faq-accordion-icon {
        color: $cms-text-secondary;
        transition: transform $cms-transition-base;
        flex-shrink: 0;
      }

      .v-icon:not(.cms-faq-accordion-icon) {
        color: $cms-primary-color;
        flex-shrink: 0;
      }

      span:not(.cms-faq-preview) {
        user-select: none;
        flex-shrink: 0;
      }

      .cms-faq-preview {
        margin-left: $cms-spacing-sm;
        font-weight: 400;
        font-size: $cms-font-size-sm;
        color: $cms-text-secondary;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
      }
    }

    .cms-faq-item-actions {
      display: flex;
      align-items: center;
      gap: $cms-spacing-xs;
      flex-shrink: 0;
    }

    .cms-btn {
      min-width: auto;
      padding: $cms-spacing-xs $cms-spacing-sm;
      height: auto;
    }
  }

  &.cms-faq-item-expanded {
    .cms-faq-accordion-icon {
      transform: rotate(90deg);
    }
  }

  .cms-faq-item-content {
    max-height: 0;
    overflow: hidden;
    transition: max-height $cms-transition-base, padding $cms-transition-base;
    padding: 0 $cms-spacing-lg;
    background: white;

    &.cms-faq-content-expanded {
      max-height: 5000px;
      padding: $cms-spacing-lg;
    }

    .cms-faq-question-wrapper {
      margin-bottom: $cms-spacing-lg;
    }
  }
}

.cms-faq-actions {
  display: flex;
  justify-content: flex-start;
  padding-top: $cms-spacing-md;
  border-top: 1px solid $cms-border-color;
  margin-top: $cms-spacing-md;
}

// Publish toggle button (unique to this view)
.cms-publish-toggle {
  margin-top: $cms-spacing-md;
  width: 100%;
}

// Media actions (unique to this view)
.cms-media-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.cms-media-disabled {
  color: $cms-text-secondary;
  font-style: italic;
}

// Required field indicator
.cms-required {
  color: $cms-danger-color;
  margin-left: 0.25rem;
}

// Button icon variant for keyword removal
.cms-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: none;
  background: $cms-danger-light;
  color: $cms-danger-solid;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $cms-danger-border;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// ============================================
// PLACEHOLDER STATES & TRANSITIONS
// ============================================

// Form section fade transition
.cms-form-section-fade-enter-active,
.cms-form-section-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.cms-form-section-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.cms-form-section-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// Placeholder sections
.cms-placeholder-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #94a3b8;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  }
}

.cms-placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 2rem;
  gap: 1.5rem;
}

.cms-placeholder-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 50%;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.cms-placeholder-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: $cms-text-primary;
  margin: 0;
}

.cms-placeholder-description {
  font-size: 0.95rem;
  color: $cms-text-secondary;
  line-height: 1.6;
  max-width: 400px;
  margin: 0;
}

.cms-placeholder-features {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 0.5rem;
}

.cms-placeholder-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  font-size: 0.85rem;
  color: $cms-text-muted-dark;
  border: 1px solid rgba(203, 213, 225, 0.5);

  :deep(.icon) {
    color: #64748b;
  }
}

// Sidebar placeholders
.cms-sidebar-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1rem;
  gap: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
}

.cms-sidebar-placeholder-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 50%;
  color: #64748b;
}

.cms-sidebar-placeholder-text {
  font-size: 0.85rem;
  color: $cms-text-secondary;
  line-height: 1.5;
  margin: 0;
}

.cms-sidebar-placeholder-hint {
  font-size: 0.75rem;
  color: $cms-text-muted;
  font-style: italic;
  margin: 0;
}

.cms-sidebar-placeholder-features {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.cms-sidebar-placeholder-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: $cms-text-muted-dark;

  :deep(.icon) {
    color: #64748b;
  }
}

// Selected items display
.cms-selected-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: $cms-spacing-md;
  margin-top: $cms-spacing-md;
}

.cms-selected-guider-item {
  display: flex;
  align-items: flex-start;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  background: $cms-background-light;
  border: 1px solid $cms-border-color;
  border-radius: $cms-radius-md;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: $cms-primary-color;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.cms-selected-guider-image {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: $cms-radius-sm;
  overflow: hidden;
  background: $cms-background-light;
  border: 1px solid $cms-border-color;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.cms-selected-guider-image-placeholder {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: $cms-radius-sm;
  background: linear-gradient(135deg, rgba($cms-primary-color, 0.1) 0%, rgba($cms-primary-color, 0.05) 100%);
  border: 1px solid $cms-border-color;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba($cms-primary-color, 0.4);
}

.cms-selected-guider-content {
  flex: 1;
  min-width: 0;
}

.cms-selected-guider-title {
  margin: 0 0 $cms-spacing-xs 0;
  font-size: $cms-font-size-base;
  font-weight: 600;
  color: $cms-text-primary;
  line-height: 1.3;
  word-wrap: break-word;
}

.cms-selected-guider-user {
  margin: 0 0 $cms-spacing-xs 0;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.4;
}

.cms-selected-guider-tagline {
  margin: 0;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  font-style: italic;
  line-height: 1.4;
  word-wrap: break-word;
}

.cms-selected-guider-remove {
  position: absolute;
  top: $cms-spacing-xs;
  right: $cms-spacing-xs;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: $cms-text-secondary;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: $cms-danger-color;
    color: white;
    transform: scale(1.1);
  }
}

// Loading state improvements
.cms-input:disabled,
.cms-select:disabled,
.cms-textarea:disabled {
  background-color: #f8fafc;
  border-color: #e2e8f0;
  color: #64748b;
  cursor: not-allowed;
}

.cms-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

// Categories section styles
.cms-categories-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cms-categories-help {
  margin: 0;
}

.cms-categories-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: $cms-spacing-sm;
  flex-wrap: wrap;
}

.cms-categories-count {
  margin-right: auto;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  line-height: 1.3;
}

.cms-categories-add-btn {
  flex-shrink: 0;
}

.cms-loading-categories {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  color: $cms-text-secondary;
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.cms-empty-categories {
  padding: 1.25rem 1rem;
  text-align: center;
  border: 1px dashed $cms-border-color;
  border-radius: $cms-radius-md;
  background: $cms-background-gray-light;

  .cms-empty-categories-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.75rem;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: $cms-background-white;
    color: $cms-text-muted;
  }
  
  .cms-empty-text {
    margin: 0 0 0.5rem 0;
    font-size: 0.9rem;
    color: $cms-text-secondary;
  }
  
  .cms-empty-hint {
    margin: 0;
    font-size: 0.85rem;
    color: $cms-text-muted;
    font-style: italic;
  }
}

.cms-categories-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 2px;
}

.cms-category-checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  background: $cms-background-gray-light;
  border: 1px solid $cms-border-color;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: $cms-background-overlay;
    border-color: $cms-primary-color;
  }
  
  .cms-checkbox {
    margin: 0;
    cursor: pointer;
    flex-shrink: 0;
  }

  .cms-category-thumb {
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: $cms-radius-sm;
    border: 1px solid $cms-border-color;
    background: $cms-background-white;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: $cms-text-muted;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .cms-category-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    align-items: flex-start;
  }
  
  .cms-checkbox-label {
    font-size: 0.9rem;
    color: $cms-text-primary;
    cursor: pointer;
    user-select: none;
    line-height: 1.25;
  }

  .cms-category-slug {
    font-size: 0.75rem;
    color: $cms-text-muted;
    line-height: 1.2;
    word-break: break-all;
  }
  
  &:has(input:checked) {
    background: rgba($cms-primary-color, 0.1);
    border-color: $cms-primary-color;
  }
}

// Responsive improvements
@media (max-width: 768px) {
  .cms-placeholder-content {
    padding: 2rem 1rem;
  }
  
  .cms-placeholder-features {
    flex-direction: column;
    align-items: center;
  }
  
  .cms-sidebar-placeholder {
    padding: 1.5rem 1rem;
  }
}
</style>
